import { request } from './../api/request';
import protooClient from "./core/protoo-client";
import * as mediasoupClient from "./core";
import { Device, BuiltinHandlerName } from "./core/Device";
import { Transport } from "./core/Transport";
import * as cookiesManager from "./cookiesManager";
import { Producer } from "./core/Producer";
import { DataProducer } from "./core/DataProducer";
import { Consumer } from "./core/Consumer";
import { DataConsumer } from "./core/DataConsumer";
import { EnhancedEventEmitter } from "./core/EnhancedEventEmitter";
import deviceInfo from "./deviceInfo";
import * as retry from "retry";
import {
  QRTCParams,
  QRTCVideoResolution,
  ResolutionInfo,
  QRTCVideoResolutionMode,
  QRTCVideoStreamType,
  QRTCVideoEncParams,
  QRTCMediaDeviceType,
  DeviceInfo,
  QRTCMediaDeviceInfo,
  QRTCVolumeInfo,
  WebCamInfo,
  NeedConsumeInfo,
  QRTCQualityInfo,
  QRTCQuality,
  QRTCVideoFillMode,
  QRTCMediaDeviceChangeEvent,
  QRTCError,
  QRTCErrorInfo,
  QRTCCodecType
} from "./RoomParams";
import { getSubSet, ping } from "./core/utils";
import SoundMeter from "./SoundMeter";
import { isIOS } from "./core/utils";
import MultiTrackAudioPlayer from "./roomHelper/MultiTrackPlayer";

import { Logger } from './core/Logger';
const logger = new Logger('RoomClient');

const PC_PROPRIETARY_CONSTRAINTS = {
  optional: [{ googDscp: true }],
};

export default class RoomClient extends EnhancedEventEmitter {
  // Closed flag.
  private _closed: boolean;
  // Loaded flag.
  private _device: DeviceInfo;
  // Whether we want to force RTC over TCP.
  private _forceTcp: boolean;
  //强制H264
  private _forceH264: boolean;

  // Whether we want DataChannels.
  // @type {Boolean}
  private _useDataChannel: boolean;
  // Custom mediasoup-client handler name (to override default browser
  // detection if desired).
  // @type {String}
  private _handlerName: string;
  // Whether simulcast or svc should be used.
  // @type {Boolean}
  private _enableWebcamLayers: boolean;

  // Whether simulcast should be used in desktop sharing.
  // @type {Boolean}
  private _enableSharingLayers: boolean;

  // Protoo URL.
  // @type {String}
  private _protooUrl: string;

  // protoo-client Peer instance.
  // @type {protooClient.Peer}
  private _protoo: any;

  // mediasoup-client Device instance.
  // @type {mediasoupClient.Device}
  private _mediasoupDevice: Device;

  // mediasoup Transport for sending.
  // @type {mediasoupClient.Transport}
  private _sendTransport: Transport;

  // Local mic mediasoup Producer.
  // @type {mediasoupClient.Producer}
  private _micProducer: Producer;

  // Local webcam mediasoup Producer.
  // @type {mediasoupClient.Producer}
  private _webcamProducer: Producer;

  // Local share mediasoup Producer.
  // @type {mediasoupClient.Producer}
  private _shareProducer: Producer;

  // Local chat DataProducer.
  // @type {mediasoupClient.DataProducer}
  private _chatDataProducer: DataProducer;

  // Local bot DataProducer.
  // @type {mediasoupClient.DataProducer}
  private _botDataProducer: DataProducer;

  //peerList
  private _peers: Map<string, any>;

  //need consume list 先用producerId做索引
  private _needConsumes: Map<string, NeedConsumeInfo>;

  //接收可能会有多个recvTransport 因为媒体服务器做了分布式
  private _recvTransports: Map<string, Transport>;

  // mediasoup Consumers.
  // @type {Map<String, mediasoupClient.Consumer>}
  private _consumers: Map<string, Consumer>;

  // mediasoup DataConsumers.
  // @type {Map<String, mediasoupClient.DataConsumer>}
  private _dataConsumers: Map<string, DataConsumer>;

  // Map of webcam MediaDeviceInfos indexed by deviceId.
  // @type {Map<String, MediaDeviceInfos>}
  private _webcams: Map<string, MediaDeviceInfo>; //找不到MediaDeviceInfos的定义啊

  // Local Webcam.
  // @type {Object} with:
  // - {MediaDeviceInfo} [device]
  // - {String} [resolution] - 'qvga' / 'vga' / 'hd'.
  private _webcam: WebCamInfo = {
    device: null,
    resolution: "qvga",
  };

  private _encoderParam: QRTCVideoEncParams = {
    preferCodec: QRTCCodecType.QRTCCodec_H264Base,
    videoResolution: QRTCVideoResolution.QRTCVideoResolution_480_360, //just like zoom
    resMode: QRTCVideoResolutionMode.QRTCVideoResolutionModeLandscape,
    videoFps: 15,
    minVideoBitrate: 200,
    maxVideoBitrate: 500,
    enableAdjustRes: true,
  };

  private _audioProfile: String = "standard"; //包含'high'和'standard'
  private _audioLevelQueryInterval: any;
  private _roomInfo: QRTCParams;
  private _audioSource: MediaStreamAudioSourceNode;
  private _audioDestination: MediaStreamAudioDestinationNode;
  private _audioContext: AudioContext;
  private _gainNode: GainNode;
  private _script: ScriptProcessorNode;
  private _audioStreamForGain: MediaStream;
  private _audioVolumn: number;
  private _audioPlayoutVolumn: number;

  //private _localQRTCView: HTMLVideoElement;

  private _remoteVideoDoms: Map<string, HTMLVideoElement>;

  private _camDeviceList: QRTCMediaDeviceInfo[];
  private _micDeviceList: QRTCMediaDeviceInfo[];
  private _speakerDeviceList: QRTCMediaDeviceInfo[];
  private _currentMicDeviceId?:string;
	private _currentSpeakerDeviceId?:string;
	private _currentCameraDeviceId?:string;
  private _voiceTimeout: any;
  private _mcuSessionId: "";
  private _audioTracks: MediaStreamTrack[] = [];
  private _multiTrackAudioplayer:MultiTrackAudioPlayer;
  constructor() {
    super();

    // Closed flag.
    // @type {Boolean}
    this._closed = false;

    // Device info.
    // @type {Object}
    this._device = deviceInfo();

    // Whether we want to force RTC over TCP.
    // @type {Boolean}
    this._forceTcp = false;

    // Force H264 codec for sending.
    this._forceH264 = true;

    // Whether we want DataChannels.
    // @type {Boolean}
    this._useDataChannel = false;

    // Custom mediasoup-client handler name (to override default browser
    // detection if desired).
    // @type {String}
    this._handlerName = "";

    // Whether simulcast or svc should be used.
    // @type {Boolean}
    this._enableWebcamLayers = false;

    // Whether simulcast should be used in desktop sharing.
    // @type {Boolean}
    this._enableSharingLayers = false;

    // Protoo URL.
    // @type {String}
    this._protooUrl = ``;

    // protoo-client Peer instance.
    // @type {protooClient.Peer}
    this._protoo = null;

    // mediasoup-client Device instance.
    // @type {mediasoupClient.Device}
    this._mediasoupDevice = null;

    // mediasoup Transport for sending.
    // @type {mediasoupClient.Transport}
    this._sendTransport = null;

    // Local mic mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._micProducer = null;

    // Local webcam mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._webcamProducer = null;

    // Local share mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._shareProducer = null;

    // Local chat DataProducer.
    // @type {mediasoupClient.DataProducer}
    this._chatDataProducer = null;

    // Local bot DataProducer.
    // @type {mediasoupClient.DataProducer}
    this._botDataProducer = null;

    //peer list
    this._peers = new Map();

    //recv transport list
    this._recvTransports = new Map();

    //need consume list
    this._needConsumes = new Map();

    // mediasoup Consumers.
    // @type {Map<String, mediasoupClient.Consumer>}
    this._consumers = new Map();

    // mediasoup DataConsumers.
    // @type {Map<String, mediasoupClient.DataConsumer>}
    this._dataConsumers = new Map();

    // Map of webcam MediaDeviceInfos indexed by deviceId.
    // @type {Map<String, MediaDeviceInfos>}
    this._webcams = new Map();

    this._roomInfo = null;

    this._audioContext = null;
    this._audioSource = null;
    this._audioDestination = null;
    this._gainNode = null;
    this._audioStreamForGain = null;
    this._audioVolumn = 0;
    this._audioPlayoutVolumn = 0;
    //this._localQRTCView = null
    this._remoteVideoDoms = new Map();
    this._voiceTimeout = null;
    this._multiTrackAudioplayer = new MultiTrackAudioPlayer();
  }

  // 	// 获取指定设备类型的数组
  // async getConnectedDevices(type: string) {
  // 	const devices = await navigator.mediaDevices.enumerateDevices();
  // 	return devices.filter(device => device.kind === type)
  //   }

  async close() {
    if (this._closed) return;

    this._closed = true;
    this.cleanStatus();

    //对新协议 要主动发送leave 此时关闭了估计异步会被杀掉因此发过去不管了
    await this.safeRequest("leave");

    // Close protoo Peer 这里加await不对
    this._protoo.close();
  }

  async exitRoom(silence: boolean = false) {
    if (this._closed) return;

    this._closed = true;
    this.cleanStatus();

    if (!silence) {
      await this.safeRequest("leave");
    }
    // Close protoo Peer 这里加await不对
    this._protoo.close();
  }

  async cleanStatus() {
    this._closed = false;
    this._device = deviceInfo();
    this._forceTcp = false;
    this._forceH264 = true;
    this._enableWebcamLayers = false;
    this._enableSharingLayers = false;
    this._mediasoupDevice = null;
    this._micProducer = null;
    this._webcamProducer = null;
    this._shareProducer = null;
    this._peers = new Map();

    this._needConsumes = new Map();
    this._consumers = new Map();
    this._webcams = new Map();

    this._audioContext = null;
    this._audioSource = null;
    this._audioDestination = null;
    this._gainNode = null;
    this._audioStreamForGain = null;
    this._audioVolumn = 0;
    this._audioPlayoutVolumn = 0;
    //this._localQRTCView = null
    this._remoteVideoDoms = new Map();
    if (this._audioContext != null && this._script != null) {
      this._audioSource.disconnect();
      this._script.disconnect();
    }
    if (this._audioLevelQueryInterval) {
      clearInterval(this._audioLevelQueryInterval);
      this._audioLevelQueryInterval = null;
    }

    if (this._voiceTimeout) {
      clearTimeout(this._voiceTimeout);
      this._voiceTimeout = null;
    }
    // Close mediasoup Transports.
    if (this._sendTransport) {
      this._sendTransport.close();
      this._sendTransport = null;
    }
    for (const recvTransport of this._recvTransports.values()) {
      recvTransport.close();
    }
    this._recvTransports = new Map();
  }

  //安全请求
  async safeRequest(method: string, data: any = undefined) {
    try {
      await this._protoo.request(method, data);
    } catch (error) {
      logger.debug(`request failed due to: ${error}`);
    }
  }

  //###########################################SDK接口开始###########################################
  //设置本地视频属性 需要在打开摄像头之前设置
  setVideoEncoderParam(encoderParam: QRTCVideoEncParams) {
    if(encoderParam.preferCodec == undefined){
			encoderParam.preferCodec = QRTCCodecType.QRTCCodec_H264Base;
		}
    this._encoderParam = Object.assign(this._encoderParam, encoderParam);
  }

  //设置本地音频的属性 需要在打开音频前设置 默认为standard
  // 音频 Profile	采样率	声道	码率(kbps)
  // standard	48000	单声道	40
  // high	48000	单声道	128
  setAudioProfile(profile: String) {
    this._audioProfile = profile;
  }

  //进入房间
  async enterRoom(roomInfo: QRTCParams) {
    // 错误-3316
    if (
      roomInfo.roomId == null ||
      roomInfo.userId == null ||
      roomInfo.userId == "" ||
      roomInfo.serverUrl == null
    ) {
      logger.debug(`enterRoom roomInfo=${JSON.stringify(roomInfo)}`);
      this.safeEmit("error", { code: -3316 });
      return;
    }
    if (roomInfo.maxRetryTime == null) {
      roomInfo.maxRetryTime = 900 * 1000;
    }

    if (roomInfo.userId.length < 4) {
      this.safeEmit("error", { code: -3319 });
      return;
    }

    if (roomInfo.roomId.length < 4) {
      this.safeEmit("error", { code: -3318 });
      return;
    }
    roomInfo.xConferenceToken = roomInfo.xConferenceToken
      ? roomInfo.xConferenceToken
      : "1234567890";
    roomInfo.avatarUrl = roomInfo.avatarUrl ? roomInfo.avatarUrl : "";
    roomInfo.acceptLanguage = roomInfo.acceptLanguage
      ? roomInfo.acceptLanguage
      : "zh-CN";
    //roomInfo.serverUrl = 'live.100tt.com.cn/sigle-server'
    this._roomInfo = roomInfo;
    this._protooUrl = `${roomInfo.serverUrl}?roomId=${roomInfo.roomId}&peerId=${roomInfo.userId}&forceH264=false&forceVP9=false`;
    logger.debug(`enterRoom url=${this._protooUrl}`);
    const protooTransport = new protooClient.WebSocketTransport(
      this._protooUrl,
      {
        clientConfig: { keepalive: true, keepaliveInterval: 30 * 1000 },
        maxRetryTime: roomInfo.maxRetryTime,
      }
    );
    this._protoo = new protooClient.Peer(protooTransport);
    //开启设备变化监测
    this.enableDevicesMonitor();

    this._protoo.on("open", async () => {
      logger.debug(`websocket connected!`);
      this.cleanStatus();
      try {
        await this._enterRoom();
      } catch (error) {
        throw (error);
      }	 
    });
    this._protoo.on("failed", () => {
      this.safeEmit("error", "WebSocket connection failed");
    });

    // 判断网抛出错误码
    this._protoo.on("error", () => {
      //this.safeEmit('error', { code: -3308 })
    });

    this._protoo.on("disconnected", () => {
      //TODO 通知连接断开
      this.safeEmit("disconnected");
      // Close mediasoup Transports.
      //this.cleanStatus();
     
      this.safeEmit("error", "room closed");
    });

    this._protoo.on("close", () => {
      logger.debug(`protoo on close!`);
      if (this._closed) return;
      this.close();
      //this.safeEmit("connectionLost");
    });

    this._protoo.on("connectionTimeOut", () => {
     
     
      logger.debug(`protoo connectionTimeOut!!!!!!`);
      this.safeEmit("connectionTimeOut");
    });

    // eslint-disable-next-line no-unused-vars
    this._protoo.on(
      "request",
      async (request: any, accept: any, reject: any) => {
        switch (request.method) {
          case "heartBeat": {
            logger.debug(`heartBeat`);
            accept();
            break;
          }
        }
      }
    );

    this._protoo.on("notification", (notification: any) => {
      logger.debug(
        `proto "notification" event [method:${notification.method}, data:${notification.data}]`
      );

      switch (notification.method) {
        case "producerCreated": {
          logger.debug(
            `[TraceVideo] producerCreated:${JSON.stringify(
              notification.data
            )}`
          );
          const { peerId, producerId, kind, paused, appData } =
            notification.data; //TODO by pcg 需要让服务器加上appData
          //这个要存一下
          this._needConsumes.set(producerId, {
            peerId,
            producerId,
            kind,
            appData,
          });
          if (!paused) {
            if (appData.share != undefined) {
              this.safeEmit("userShareAvailable", {
                userId: peerId,
                available: true,
              });
            } else {
              this.safeEmit(
                kind == "video" ? "userVideoAvailable" : "userAudioAvailable",
                {
                  userId: peerId,
                  available: true,
                }
              );
            }
          }
          break;
        }
        case "newPeerJoined": {
          logger.debug(
            `newPeerJoined:${JSON.stringify(notification.data)}`
          );
          const { peerId, displayName, device, timeMs, avatarUrl } =
            notification.data;
          this._peers.set(peerId, notification.data);

          //有种情况,某个用户的浏览器刷新，他的peerId没变，然后重新进房，但是我这边存
          //的还是他以前的消费信息，此时要先把之前的同样的peerId对应的消费信息清除掉,这样才能重新订阅正确的
          for (const needConsumerValue of this._needConsumes.values()) {
            if (peerId == needConsumerValue.peerId) {
              this._needConsumes.delete(needConsumerValue.producerId);
            }
          }
          for (const consumerValue of this._consumers.values()) {
            if (peerId == consumerValue.appData.peerId) {
              this._consumers.delete(consumerValue.id);
            }
          }

          this.safeEmit("remoteUserEnterRoom", {
            userId: peerId,
            userName: displayName,
            userDevice: device,
            timeMs,
            avatarUrl,
          });
          break;
        }
        case "peerLeft": {
          logger.debug(
            `peerLeft:${JSON.stringify(notification.data)}`
          );
          const { peerId } = notification.data;
          this._peers.delete(peerId);
          //有种情况,远端的用户突然信令断了,然后再连上,本地有可能收不到consumerClosed的消息，此时要先把
          //之前的同样的peerId对应的消费信息清除掉,这样才能重新订阅正确的
          for (const needConsumerValue of this._needConsumes.values()) {
            if (peerId == needConsumerValue.peerId) {
              this._needConsumes.delete(needConsumerValue.producerId);
            }
          }
          for (const consumerValue of this._consumers.values()) {
            if (peerId == consumerValue.appData.peerId) {
              this._consumers.delete(consumerValue.id);
            }
          }

          this.safeEmit("remoteUserLeaveRoom", {
            userId: peerId,
          });
          break;
        }
        case "producerScore": {
          const { producerId, score } = notification.data;
          let quality: QRTCQuality = QRTCQuality.QRTCQuality_Good;
          if (score == 0) {
            quality = QRTCQuality.QRTCQuality_Down;
          } else if (score > 0 && score <= 2) {
            quality = QRTCQuality.QRTCQuality_Vbad;
          } else if (score > 2 && score <= 4) {
            quality = QRTCQuality.QRTCQuality_Bad;
          } else if (score > 4 && score <= 6) {
            quality = QRTCQuality.QRTCQuality_Poor;
          } else if (score > 6 && score <= 9) {
            quality = QRTCQuality.QRTCQuality_Good;
          } else if (score == 10) {
            quality = QRTCQuality.QRTCQuality_Excellent;
          }
          const qualityInfo: QRTCQualityInfo = {
            userId: this._roomInfo.userId,
            quality: quality,
          };
          this.safeEmit("networkQuality", qualityInfo);
          //const { producerId, score } = notification.data;

          break;
        }
        case "displayNameChanged": {
          const { peerId, displayName, oldDisplayName } = notification.data;
          this.safeEmit("displayNameChanged", {
            peerId,
            displayName,
            oldDisplayName,
          });
          break;
        }

        case "consumerClosed": {
          //代表某一路音频或者视频被关闭
          const { peerId, producerId, consumerId } = notification.data;
          //已经关了不需要再消费了
          this._needConsumes.delete(producerId);
          //cosumerClosed:{"consumerId":"7f83e645-4f87-49eb-bfe0-4ce35160e62b"}
          logger.debug(
            "cosumerClosed:" + JSON.stringify(notification.data)
          );
          const consumer = this._consumers.get(consumerId);
          if (!consumer) break;
          consumer.close();
          this._consumers.delete(consumerId);
          this._remoteVideoDoms.delete(peerId);

          //TODO
          if (consumer.appData.share != undefined) {
            logger.debug(`userShareAvailable false`);
            this.safeEmit("userShareAvailable", {
              userId: peerId,
              available: false, //不存在了
              paused: false,
            });
          } else {
            if(this._multiTrackAudioplayer && consumer.kind == "audio"){
              this._multiTrackAudioplayer.removeTrack(consumer.track);
            }
            this.safeEmit(
              consumer.kind == "video"
                ? "userVideoAvailable"
                : "userAudioAvailable",
              {
                userId: peerId,
                available: false, //不存在了
              }
            );
          }
          break;
        }

        case "consumerPaused": {
          const { consumerId, peerId, appData, kind, producerId } =
            notification.data;
          logger.debug(
            `[RoomClient bug] consumerPaused with data=${JSON.stringify(
              notification.data
            )}`
          );
          const consumer = this._consumers.get(consumerId);

          if (!consumer) {
            if (kind == "audio") {
              //logger.debug(`[RoomClient AudioTrace] consumerPaused not found consumer for user:${peerId} consumerId:${consumerId}`, true)
            } else {
              consumer.pause();
            }
            //logger.debug(`consumerPaused not found consumer`);
          }

          if (appData != undefined && appData.share != undefined) {
            this.safeEmit("userShareAvailable", {
              userId: peerId,
              // available: false, //不存在了
              available: true, // TODO: 这里待和潘哥确认，先改为true
              paused: true,
              //appData,
              //producerId,
              //status: 3
            });
            //consumer.SetRemotePaused(true);
          } else {
            if (kind == "audio") {
              //logger.debug(`[RoomClient AudioTrace] consumerPaused emit userAudioAvailable for user:${peerId}`, true)
            }
            this.safeEmit(
              kind == "video" ? "userVideoAvailable" : "userAudioAvailable",
              {
                userId: peerId,
                available: false, //不存在了
                appData,
                producerId,
              }
            );
          }
          break;
        }

        case "consumerResumed": {
          logger.debug(
            `consumerResumed with notification data=${JSON.stringify(
              notification.data
            )}`
          );
          const { peerId, consumerId, appData, kind, producerId } =
            notification.data;
          this._needConsumes.set(producerId, {
            peerId,
            producerId,
            kind,
            appData,
          });

          const consumer = this._consumers.get(consumerId);

          //找不到,则代表还没有消费过这路流,因此需要触发主动消费
          if (!consumer) {
            if (kind == "audio") {
              //logger.debug(`[RoomClient AudioTrace] consumerResumed have not consume this stream for user:${peerId}`, true)
            }
            //logger.debug(`[RoomClient AudioTrace] consumerResumed have not consume this stream for user:${peerId}`)
          } else {
            consumer.resume();
            if (kind == "audio") {
            }
          }

          if (appData != undefined && appData.share != undefined) {
            this.safeEmit("userShareAvailable", {
              userId: peerId,
              available: true,
              //appData,
              //producerId,
              //status: 4
            });
          } else {
            if (kind == "audio") {
              logger.debug(
                `[RoomClient AudioTrace] emit userAudioAvailable for user:${peerId}`
              );
            }
            this.safeEmit(
              kind == "video" ? "userVideoAvailable" : "userAudioAvailable",
              {
                userId: peerId,
                available: true,
                //appData,
                //producerId
              }
            );
          }

          //找到,则代表这路流已经消费过了,需要将对应的consumer变为resumed
          //consumer.resume();
          //然后通知pause状态变为resume
          break;
        }

        case "consumerLayersChanged": {
          // const { consumerId, spatialLayer, temporalLayer } = notification.data;
          // const consumer = this._consumers.get(consumerId);

          // if (!consumer)
          // 	break;

          break;
        }

        case "consumerScore": {
          // const { consumerId, score } = notification.data;
          break;
        }

        case "activeSpeaker": {
          // const { peerId } = notification.data;
          break;
        }
        case "voiceEnergyNotify": {
          // console.log("语音激励voiceEnergyNotify")
          const { voiceEnergy } = notification.data;
          let maxEnergyValue = {
            action: "start",
            peerID: "1",
            voice: 0,
          };
          for (const voiceEnergyValue of voiceEnergy) {
            if (
              voiceEnergyValue.voice > maxEnergyValue.voice &&
              (voiceEnergyValue.action == "start" ||
                voiceEnergyValue.action == "unmute")
            ) {
              maxEnergyValue = voiceEnergyValue;
            }
            if (
              voiceEnergyValue.action == "end" ||
              voiceEnergyValue.action == "mute"
            ) {
              //语音激励回调,返回2个参数，第一个参数是发言人的id, 第2个参数是 发言人的能量有多大
              this.safeEmit("userSpeaking", {
                userId: voiceEnergyValue.peerID,
                action: voiceEnergyValue.action,
              });
            }
          }
          this.safeEmit("userSpeaking", {
            userId: maxEnergyValue.peerID,
            action: maxEnergyValue.action,
          });
          break;
        }
        case "rtcControlNotify": {
          this.safeEmit("customCommand", notification.data);
          break;
        }

        case "peerKicked": {
          //用户被踢出房间
          const { peerId } = notification.data;
          let isMe = this._roomInfo.userId == peerId;

          if (isMe) {
            this.safeEmit("exitRoom", {
              reason: 1, //1：被服务器踢出当前房间；2：主持人主动结束当前会议EndConference 3.服务器强制结束当前会议forceEndConference
            });
          } else {
            this.safeEmit("remoteUserLeaveRoom", {
              userId: peerId,
              reason: 2, //0 表示用户主动退出房间，1 表示用户超时退出，2 表示被踢出房间
            });
          }

          break;
        }
        case "roomDestroyed": {
          //用户被踢出房间
          const { fromPeerId, reason } = notification!["data"];
          let isForce = reason == "forceEndConference";
          this.safeEmit("exitRoom", {
            reason: isForce ? 3 : 2, //1：被服务器踢出当前房间；2：主持人主动结束当前会议EndConference 3.服务器强制结束当前会议forceEndConference
          });
          break;
        }
        case "hostLeaveMsg":{
          console.log("hostLeaveMsg::::", "host leave !!!")
          const { peerId, orderId, seq, data } = notification.data;
          this.safeEmit("recvHostLeaveMsg", {
            peerId,
            orderId,
            seq,
            data,
          });
          break;
        }
        case "customCmdMsg": {
          //白板自定义协议
          const { peerId, cmdId, seq, data } = notification.data;
          this.safeEmit("recvCustomCmdMsg", {
            peerId,
            cmdId,
            seq,
            data,
          });
          break;
        }

        default: {
          logger.debug(
            `unknown protoo notification.method ${notification.method}`
          );
        }
      }
    });
  }

  //开启本地视频
  async startLocalPreview(qrtcView?: HTMLVideoElement) {
    //先判断是否有sendTransport
    await this._createSendTransport();
    //然后开启摄像头
    const devicesCookie = cookiesManager.getDevices();
    if (!devicesCookie || devicesCookie.webcamEnabled) {
      await this.enableWebcam(qrtcView);
    }
  }

  //关闭本地视频
  async stopLocalPreview() {
    this.disableWebcam();
  }

  //暂停本地视频
  async muteLocalVideo(mute: boolean) {
    if (!this._webcamProducer) {
      return;
    }
    if (mute) {
      this._webcamProducer.pause();
      try {
        await this._protoo.request("pauseProducer", {
          transportId: this._sendTransport.id,
          transportType: "webrtc",
          producerId: this._webcamProducer.id,
        });
      } catch (error) {
        logger.debug(`muteLocalVideo failed:${error}`);
      }
    } else {
      this._webcamProducer.resume();

      try {
        await this._protoo.request("resumeProducer", {
          transportId: this._sendTransport.id,
          transportType: "webrtc",
          producerId: this._webcamProducer.id,
        });
      } catch (error) {
        logger.debug(`unMuteLocalVideo failed:${error}`);
      }
    }
  }

  //开启本地音频
  async startLocalAudio(mute: boolean=false) {
    await this._createSendTransport();
    //然后开启麦克风
    this.enableMic();
  }

  //关闭本地音频
  async stopLocalAudio() {
    this.disableMic();
  }

  //静音/取消静音本地音频
  async muteLocalAudio(mute: boolean=false) {
    if (!this._micProducer) {
      await this.startLocalAudio(mute);
      return;
    }
    if (mute) {
      this._micProducer.pause();
      try {
        await this._protoo.request("pauseProducer", {
          transportId: this._sendTransport.id,
          transportType: "webrtc",
          producerId: this._micProducer.id,
        });
      } catch (error) {
        logger.debug(`muteMic() | failed: ${error}`);
      }
    } else {
      this._micProducer.resume();

      try {
        await this._protoo.request("resumeProducer", {
          transportId: this._sendTransport.id,
          transportType: "webrtc",
          producerId: this._micProducer.id,
        });
      } catch (error) {
        logger.debug(`unmuteMic() | failed: ${error}`);
      }
    }
    //this._audioLocalMute = mute;
  }

  async publishVideo(): Promise<MediaStreamTrack>{
    //先判断是否有sendTransport
    await this._createSendTransport();
    //如果摄像头已开启，则直接返回对应track
    if (this._webcamProducer) {
      return this._webcamProducer.track;
    }
    //如果设备有问题或没有摄像头，则报错
    if (!this._mediasoupDevice.canProduce("video")) {
      logger.debug("enableWebcam() | cannot produce video");
      return Promise.reject(new Error('can not produce video'));
    }
    let track;
    let device;
    try {
      await this._updateWebcams();
      device = this._webcam.device;
      if (!device){
        return Promise.reject(new Error('can not produce video'));
      }

      logger.debug("enableWebcam() | calling getUserMedia()");

      let resolutionInfo = this.getResolutionInfo(
        this._encoderParam.videoResolution
      );
      let fps = this._encoderParam.videoFps;
      var stream: any = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { ideal: device.deviceId },
            width: { ideal: resolutionInfo.resWidth },
            height: { ideal: resolutionInfo.resHeight },
            frameRate: fps,
          },
        });
      } catch (error: any) {
        if (error.name === "NotAllowedError") {
          return Promise.reject(new QRTCError('NotAllowedError', "", QRTCErrorInfo.ERR_CAMERA_NO_GRANT));
        } else if (error.name === "AbortError") {
          return Promise.reject(new QRTCError('AbortError', "", QRTCErrorInfo.ERR_CAMERA_NO_GRANT));
        } else if (error.name === "OverConstrainedError") {
          return Promise.reject(new QRTCError('OverConstrainedError', "", QRTCErrorInfo.ERR_CAMERA_NO_GRANT));
        } else if (error.name === "NotReadableError") {
          return Promise.reject(new QRTCError('NotReadableError', "", QRTCErrorInfo.ERR_CAMERA_NO_GRANT));
        }
      }

      track = stream.getVideoTracks()[0];

      let encodings;
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };

      if (this._forceH264) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
          (c) => c.mimeType.toLowerCase() === "video/h264"
        );

        if (!codec) {
          return Promise.reject(new QRTCError('desired H264 codec+configuration is not supported', "", QRTCErrorInfo.ERR_CODEC_NOT_SUPPROT));
        }
      }

      //设置了大小流模式并且用户设置的分辨率能支持大小流
      if (this._enableWebcamLayers && resolutionInfo.canScaleDown) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec =
          this._mediasoupDevice.rtpCapabilities.codecs.find(
            (c) => c.kind === "video"
          );

        //let encodings;
        let simulcastInfo = [
          {
            scaleResolutionDownBy: resolutionInfo.scaleDownBy,
            maxBitrate: resolutionInfo.maxDownBitrate * 1000,
          },
          {
            scaleResolutionDownBy: 1,
            maxBitrate: resolutionInfo.maxUpBitrate * 1000,
          },
        ];
        encodings = simulcastInfo;

        this._webcamProducer = await this._sendTransport.produce({
          track,
          encodings,
          codecOptions: {
            videoGoogleStartBitrate: 1000,
          },
          // NOTE: for testing codec selection.
          codec: this._mediasoupDevice.rtpCapabilities.codecs.find(
            (codec) => codec.mimeType.toLowerCase() === "video/h264"
          ),
          appData: {
            peerName: this._roomInfo.userName,
            peerId: this._roomInfo.userId,
          },
        });
      } else {
        //TODO 确保这里不是simulcast
        let encodeInfo = [
          { maxBitrate: this._encoderParam.maxVideoBitrate * 1000 },
        ];
        encodings = encodeInfo;
        this._webcamProducer = await this._sendTransport.produce({
          track,
          encodings,
          codecOptions,
          codec,
          appData: {
            peerName: this._roomInfo.userName,
            peerId: this._roomInfo.userId,
          },
        });
      }

      logger.debug(`emit localVideoTrack event`);

      this._webcamProducer.on("transportclose", () => {
        logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
        this._webcamProducer = null;
      });

      this._webcamProducer.on("trackended", () => {
        logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
        this.disableWebcam().catch(() => { });
      });

      return this._webcamProducer.track;
    } catch (error) {
      if (track) track.stop();
      return Promise.reject(new QRTCError('other error', `enableWebcam() | failed:${error}`, QRTCErrorInfo.ERR_OTHERS));
    }
  }

  async unpublishVideo(): Promise<void>{
    if (!this._webcamProducer){
      return Promise.reject(new Error('can not produce video'));
    } 
    this._webcamProducer.close();
    try {
      await this._protoo.request("closeProducer", {
        transportId: this._sendTransport.id,
        transportType: "webrtc",
        producerId: this._webcamProducer.id,
      });
    } catch (error) {
      return Promise.reject(new Error('request closeProducer failed'));
    }
    this._webcamProducer = null;
    return;
  }

  async publishShare(){

  } 

  async unpublishShare(){

  }

  //拉取远端的音频流返回track
  async subscribeAudio(userId: string): Promise<MediaStreamTrack> {
    //两种情况 一种是这个音频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "audio"
      ) {
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      //发送消息resume该音频
      await this._resumeConsumer(consumer);
      return Promise.reject(new Error('audio already consumed'));
    }

    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "audio"
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `startRemoteAudio userId=${userId} needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }
    if (!needConsumeInfo) {
      return Promise.reject(new Error(`can not consume audio needConsumeInfo=${needConsumeInfo}`));
    }
    let transportId = await this._createRecvTransport(
      needConsumeInfo.producerId
    );
    //发送消费请求
    logger.debug(
      `createRecvTransport userId=${userId} transportId=${JSON.stringify(
        transportId
      )}`
    );
    const consumerInfo = await this._protoo.request("consume", {
      transportId: transportId,
      transportType: "webrtc",
      producerId: needConsumeInfo.producerId,
      rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
      paused: true,
      appData: {
        peerId: userId,
        share: false,
      },
    });
    logger.debug(
      `startRemoteAudio userId=${userId} consume result=${JSON.stringify(
        consumerInfo
      )}`
    );
    //开始消费
    const { id, producerId, kind, producerPaused, rtpParameters, type } =
      consumerInfo;
    try {
      const consumer = await this._recvTransports.get(transportId).consume({
        id,
        recvTransportId: transportId,
        producerId,
        kind,
        rtpParameters,
        //codecOptions, TODO make sure this is redundant
        appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
      });
      logger.debug(
        `startRemoteAudio userId=${userId} consumer created with transportId=${consumer.recvTransportId}`
      );
      // Store in the map.
      this._consumers.set(consumer.id, consumer);

      consumer.on("transportclose", () => {
        logger.debug(
          `startRemoteAudio userId=${userId} transportclose`
        );
        this._consumers.delete(consumer.id);
      });
      //如果服务器pause住关键帧,则要发送resume
      await this._protoo.request("resumeConsumer", {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        consumerId: consumer.id,
      });
      if(this._multiTrackAudioplayer){
        this._multiTrackAudioplayer.addTrack(consumer.track);
      }
      return consumer.track;
    } catch (error) {
      logger.debug(`"newConsumer" request failed:${error}`);
      throw error;
    }
  }

  async unsubscribeAudio(userId: string): Promise<void> {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (userId == consumer.appData.peerId && consumer.kind == "audio") {
        consumer = consumerValue;
        logger.debug(
          `stopRemoteAudio consumeInfo=${JSON.stringify(consumer)}`
        );
        break;
      }
    }
    if (consumer) {
      consumer.close();
      this._consumers.delete(consumer.id);
    }
  }

  //拉取远端peer的视频返回track
  async subscribeVideo(userId: string): Promise<MediaStreamTrack> {
    //两种情况 一种是这个视频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    logger.debug(`[TraceVideo] subscribeVideo userId=${userId}`);
    var consumer: Consumer = null;
    logger.debug(`[TraceVideo] this._consumers.size=${this._consumers.size}`);
    for (const consumerValue of this._consumers.values()) {
      logger.debug(`[TraceVideo] subscribeVideo consumerValue=${JSON.stringify(consumerValue)}`);
      if (userId == consumerValue.appData.peerId && consumerValue.kind == "video" && consumerValue.appData.share == undefined) {
        logger.debug(`[TraceVideo] subscribeVideo repeated find userId=${userId}`);
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      logger.debug(`[TraceVideo] subscribeVideo repeated userId=${userId}`);
      return Promise.reject(new Error('video already consumed'));
      //return consumer.track;
    }
    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "video" &&
        needConsumerValue.appData.share == undefined
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `[TraceVideo] subscribeVideo needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }
    if (!needConsumeInfo) {
      return Promise.reject(new Error(`can not consume video needConsumeInfo=${needConsumeInfo}`));
    }

    let transportId = await this._createRecvTransport(
      needConsumeInfo.producerId
    );
    //发送消费请求
    logger.debug(
      `[TraceVideo] createRecvTransport transportId=${JSON.stringify(
        transportId
      )}`
    );
    let consumeRequest = {
      transportId: transportId,
      transportType: "webrtc",
      producerId: needConsumeInfo.producerId,
      rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
      appData: {
        peerId: userId,
      },
    };

    const consumerInfo = await this._protoo.request("consume", {
      transportId: transportId,
      transportType: "webrtc",
      producerId: needConsumeInfo.producerId,
      rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
      appData: {
        peerId: userId,
      },
    });

    //开始消费
    const {
      id,
      producerId,
      kind,
      //producerPaused, //这个跟医百的不一样了,我们不处理producerPaused
      rtpParameters,
      type,
    } = consumerInfo;
    try {
      const consumer = await this._recvTransports.get(transportId).consume({
        id,
        recvTransportId: transportId,
        producerId,
        kind: "video", //TODO respond's kind always audio so direct set as "video"
        rtpParameters,
        //codecOptions, TODO make sure this is redundant
        appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
      });
      logger.debug(
        `[TraceVideo] RoomClient] startRemoteVideo consumer created with transportId=${consumer.recvTransportId}`
      );
      logger.debug(`[TraceVideo] startRemoteVideo add consumer id=${consumer.id} to this._consumers`);
      // Store in the map.
      this._consumers.set(consumer.id, consumer);
      logger.debug(`[TraceVideo] this._consumers.size2=${this._consumers.size}`);

      consumer.on("transportclose", () => {
        this._consumers.delete(consumer.id);
        logger.debug(
          `[TraceVideo] transportClosed with consumer id=${consumer.id}`
        );
      });

      //如果服务器pause住关键帧,则要发送resume
      await this._resumeConsumer(consumer);
      return consumer.track;
    } catch (error) {
      return Promise.reject(error);
    }

    
  }

  async unsubscribeVideo(userId: string): Promise<void> {
    try{
      await this.stopRemoteVideo(userId);
    }catch(error){
      throw(error);
    }
  }

  async subscribeShare(userId: string): Promise<MediaStreamTrack> {
    //两种情况 一种是这个视频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share != undefined
      ) {
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      //发送消息resume该视频
      await this._resumeConsumer(consumer);
      return Promise.reject(new Error('share already consumed'));
    }
    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "video" &&
        needConsumerValue.appData.share != undefined
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `startRemoteShare needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }
    if (!needConsumeInfo) {
      return Promise.reject(new Error(`can not consume share needConsumeInfo=${needConsumeInfo}`));
    }
      let peerId = needConsumeInfo.peerId;
      let transportId = await this._createRecvTransport(
        needConsumeInfo.producerId
      );
      //发送消费请求
      logger.debug(
        `createRecvTransport transportId=${JSON.stringify(
          transportId
        )}`
      );
      const consumerInfo = await this._protoo.request("consume", {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        appData: { ...needConsumeInfo.appData, peerId },
      });

      //开始消费
      const { id, producerId, kind, producerPaused, rtpParameters, type } =
        consumerInfo;
      try {
        const consumer = await this._recvTransports.get(transportId).consume({
          id,
          recvTransportId: transportId,
          producerId,
          kind,
          rtpParameters,
          //codecOptions, TODO make sure this is redundant
          appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
        });

        // Store in the map.
        this._consumers.set(consumer.id, consumer);

        consumer.on("transportclose", () => {
          this._consumers.delete(consumer.id);
        });

        //如果服务器pause住关键帧,则要发送resume
        if (producerPaused) {
          await this._protoo.request("resumeConsumer", {
            transportId: transportId,
            transportType: "webrtc",
            producerId: needConsumeInfo.producerId,
            consumerId: consumer.id,
          });
        }
        return consumer.track;
      } catch (error) {
        logger.debug(`"newConsumer" request failed:${error}`);
        throw error;
      }

  }

  async unsubscribeShare(userId: string): Promise<void> {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumer.appData.peerId &&
        consumer.kind == "video" &&
        consumer.appData.share != undefined
      ) {
        consumer = consumerValue;
        logger.debug(
          `stopRemoteShare consumeInfo=${JSON.stringify(consumer)}`
        );
        break;
      }
    }
    if (consumer) {
      consumer.close();
      this._consumers.delete(consumer.id);
    }
  }
  
  // //拉取远端peer的视频返回track
  // async subscribeVideo(userId: string):Promise<MediaStreamTrack> {
  //   //两种情况 一种是这个视频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
  //   return new Promise(async (resolve, reject) => {
  //     logger.debug(`[TraceVideo] subscribeVideo userId=${userId}`);
  //     var consumer: Consumer = null;
  //     logger.debug(`[TraceVideo] this._consumers.size=${this._consumers.size}`);
  //     for (const consumerValue of this._consumers.values()) {
  //       logger.debug(`[TraceVideo] subscribeVideo consumerValue=${JSON.stringify(consumerValue)}`);
  //       if(userId == consumerValue.appData.peerId && consumerValue.kind == "video" && consumerValue.appData.share == undefined)
  //       {
  //         logger.debug(`[TraceVideo] subscribeVideo repeated find userId=${userId}`);
  //         consumer = consumerValue;
  //         break;
  //       }
  //     }
  //     if (consumer) {
  //       logger.debug(`[TraceVideo] subscribeVideo repeated userId=${userId}`);
  //       this.safeEmit("userVideoSubscribed", {userId, track: consumer.track});
  //       resolve(``);
  //       return;
  //     }
  //     //另一种情况是没有消费,因此需要进行消费
  //     var needConsumeInfo: NeedConsumeInfo = null;
  //     for (const needConsumerValue of this._needConsumes.values()) {
  //       if (
  //         userId == needConsumerValue.peerId &&
  //         needConsumerValue.kind == "video" &&
  //         needConsumerValue.appData.share == undefined
  //       ) {
  //         needConsumeInfo = needConsumerValue;
  //         logger.debug(
  //           `[TraceVideo] subscribeVideo needConsumeInfo=${JSON.stringify(
  //             needConsumeInfo
  //           )}`
  //         );
  //         break;
  //       }
  //     }
  
  //     if (needConsumeInfo) {
  //       let transportId = await this._createRecvTransport(
  //         needConsumeInfo.producerId
  //       );
  //       //发送消费请求
  //       logger.debug(
  //         `[TraceVideo] createRecvTransport transportId=${JSON.stringify(
  //           transportId
  //         )}`
  //       );
  //       let consumeRequest = {
  //         transportId: transportId,
  //         transportType: "webrtc",
  //         producerId: needConsumeInfo.producerId,
  //         rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
  //         appData: {
  //           peerId: userId,
  //         },
  //       };
  //       logger.debug(
  //         `consumeRequest=${JSON.stringify(consumeRequest)}`
  //       );
  //       const consumerInfo = await this._protoo.request("consume", {
  //         transportId: transportId,
  //         transportType: "webrtc",
  //         producerId: needConsumeInfo.producerId,
  //         rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
  //         appData: {
  //           peerId: userId,
  //         },
  //       });
  //       logger.debug(
  //         `[TraceVideo] consume result=${JSON.stringify(
  //           consumerInfo
  //         )}`
  //       );
  //       //开始消费
  //       const {
  //         id,
  //         producerId,
  //         kind,
  //         //producerPaused, //这个跟医百的不一样了,我们不处理producerPaused
  //         rtpParameters,
  //         type,
  //       } = consumerInfo;
  //       try {
  //         const consumer = await this._recvTransports.get(transportId).consume({
  //           id,
  //           recvTransportId: transportId,
  //           producerId,
  //           kind: "video", //TODO respond's kind always audio so direct set as "video"
  //           rtpParameters,
  //           //codecOptions, TODO make sure this is redundant
  //           appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
  //         });
  //         logger.debug(
  //           `[TraceVideo] RoomClient] startRemoteVideo consumer created with transportId=${consumer.recvTransportId}`
  //         );
  //         logger.debug(`[TraceVideo] startRemoteVideo add consumer id=${consumer.id} to this._consumers`);
  //         // Store in the map.
  //         this._consumers.set(consumer.id, consumer);
  //         logger.debug(`[TraceVideo] this._consumers.size2=${this._consumers.size}`);
  
  //         consumer.on("transportclose", () => {
  //           this._consumers.delete(consumer.id);
  //           logger.debug(
  //             `[TraceVideo] transportClosed with consumer id=${consumer.id}`
  //           );
  //         });
  
  //         //如果服务器pause住关键帧,则要发送resume
  //         //if (producerPaused) {
  //         await this._resumeConsumer(consumer);
  //         //}
  //         this.safeEmit("userVideoSubscribed", {userId, track: consumer.track});
  //         resolve(``);
  //             return;
  //       } catch (error) {
  //         logger.debug(`"newConsumer" request failed:${error}`);
  //         reject(error);
  //         return;
  //       }
  //     }
  //   });
  // }

  //主动拉远端peer的视频
   async startRemoteVideo(userId: string, qrtcView?: HTMLVideoElement) {
    //两种情况 一种是这个视频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    return new Promise(async (resolve, reject) => {
    logger.debug(`[TraceVideo] startRemoteVideo userId=${userId}`);
    var consumer: Consumer = null;
    logger.debug(`[TraceVideo] this._consumers.size=${this._consumers.size}`);
    for (const consumerValue of this._consumers.values()) {
      logger.debug(`[TraceVideo] startRemoteVideo consumerValue=${JSON.stringify(consumerValue)}`);
      if(userId == consumerValue.appData.peerId && consumerValue.kind == "video" && consumerValue.appData.share == undefined)
      {
        logger.debug(`[TraceVideo] startRemoteVideo repeated find userId=${userId}`);
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      logger.debug(`[TraceVideo] startRemoteVideo repeated userId=${userId}`);
      //发送消息resume该视频
      //await this._resumeConsumer(consumer);
      if (qrtcView) {
        logger.debug(`[TraceVideo] startRemoteVideo repeated and before play userId=${userId}`);
        const stream = new MediaStream();
        stream.addTrack(consumer.track);
        this._remoteVideoDoms.set(userId, qrtcView);
        (qrtcView as any).srcObject = stream;
        (qrtcView as any)
          .play()
          .catch((error: any) =>
            logger.debug("videoElem.play() failed:" + error)
          );
          logger.debug(`[TraceVideo] startRemoteVideo repeated and after play userId=${userId}`);
      }
      resolve(``);
      return;
    }
    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "video" &&
        needConsumerValue.appData.share == undefined
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `[TraceVideo] startRemoteVideo needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }

    if (needConsumeInfo) {
      let transportId = await this._createRecvTransport(
        needConsumeInfo.producerId
      );
      //发送消费请求
      logger.debug(
        `[TraceVideo] createRecvTransport transportId=${JSON.stringify(
          transportId
        )}`
      );
      let consumeRequest = {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        appData: {
          peerId: userId,
        },
      };
      logger.debug(
        `consumeRequest=${JSON.stringify(consumeRequest)}`
      );
      const consumerInfo = await this._protoo.request("consume", {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        appData: {
          peerId: userId,
        },
      });
      logger.debug(
        `[TraceVideo] consume result=${JSON.stringify(
          consumerInfo
        )}`
      );
      //开始消费
      const {
        id,
        producerId,
        kind,
        //producerPaused, //这个跟医百的不一样了,我们不处理producerPaused
        rtpParameters,
        type,
      } = consumerInfo;
      try {
        const consumer = await this._recvTransports.get(transportId).consume({
          id,
          recvTransportId: transportId,
          producerId,
          kind: "video", //TODO respond's kind always audio so direct set as "video"
          rtpParameters,
          //codecOptions, TODO make sure this is redundant
          appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
        });
        logger.debug(
          `[TraceVideo] RoomClient] startRemoteVideo consumer created with transportId=${consumer.recvTransportId}`
        );
        logger.debug(`[TraceVideo] startRemoteVideo add consumer id=${consumer.id} to this._consumers`);
        // Store in the map.
        this._consumers.set(consumer.id, consumer);
        logger.debug(`[TraceVideo] this._consumers.size2=${this._consumers.size}`);

        consumer.on("transportclose", () => {
          this._consumers.delete(consumer.id);
          logger.debug(
            `[TraceVideo] transportClosed with consumer id=${consumer.id}`
          );
        });

        //如果服务器pause住关键帧,则要发送resume
        //if (producerPaused) {
        await this._resumeConsumer(consumer);
        //}

        if (qrtcView) {
          const stream = new MediaStream();
          stream.addTrack(consumer.track);
          this._remoteVideoDoms.set(userId, qrtcView);
          (qrtcView as any).srcObject = stream;
          logger.debug(`[TraceVideo] 2 before play userId=${userId}`);
          (qrtcView as any)
            .play()
            .catch((error: any) =>
              logger.debug("videoElem.play() failed:" + error)
            );
            logger.debug(`[TraceVideo] 2 after play userId=${userId}`);
            resolve(``);
            return;
        }
      } catch (error) {
        logger.debug(`"newConsumer" request failed:${error}`);
        reject(error);
        return;
      }
    }
  });

  }

  //停止拉取远端peer的视频
	async stopRemoteVideo(userId: string) {
		var consumer: Consumer = null;
		for(const consumerValue of this._consumers.values()){
			if (userId == consumerValue.appData.peerId && consumerValue.kind == 'video' && consumerValue.appData.share == undefined) {
				consumer = consumerValue
				logger.debug(`stopRemoteVideo consumeInfo=${JSON.stringify(consumer)}`)
				break
			}
		}
		if (consumer) {
			await this._protoo.request(
				'closeConsumer',
				{
					transportId: consumer.recvTransportId,
					transportType: 'webrtc',
					producerId: consumer.producerId,
					consumerId: consumer.id,
				});
			consumer.close()

			this._consumers.delete(consumer.id);
		}
	}

  async stopRemoteAudio(userId: string) {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (userId == consumer.appData.peerId && consumer.kind == "audio") {
        consumer = consumerValue;
        logger.debug(
          `stopRemoteAudio consumeInfo=${JSON.stringify(consumer)}`
        );
        break;
      }
    }
    if (consumer) {
      consumer.close();
      this._consumers.delete(consumer.id);
    }
  }

  // 停止显示所有远端视频
  async stopAllRemoteViews() {
    for (var consumerInfo of this._consumers.values()) {
      await this._protoo.request("closeConsumer", {
        transportId: consumerInfo.recvTransportId,
        transportType: "webrtc",
        producerId: consumerInfo.producerId,
        consumerId: consumerInfo.id,
        appData: {},
      });
    }
  }

  //主动拉远端peer的音频
  async startRemoteAudio(userId: string, qrtcAudio: HTMLAudioElement) {
    logger.debug(`startRemoteAudio begin userId=${userId}`);
    //两种情况 一种是这个音频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "audio"
      ) {
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      //发送消息resume该音频
      await this._resumeConsumer(consumer);
      const stream = new MediaStream();
      stream.addTrack(consumer.track);
      (qrtcAudio as any).srcObject = stream;
      (qrtcAudio as any)
        .play()
        .catch((error: any) =>
          logger.debug("audioElem.play() failed:" + error)
        );
      logger.debug(
        `startRemoteAudio consumer already existed userId=${userId}`
      );
      return;
    }

    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "audio"
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `startRemoteAudio userId=${userId} needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }
    if (needConsumeInfo) {
      let transportId = await this._createRecvTransport(
        needConsumeInfo.producerId
      );
      //发送消费请求
      logger.debug(
        `createRecvTransport userId=${userId} transportId=${JSON.stringify(
          transportId
        )}`
      );
      const consumerInfo = await this._protoo.request("consume", {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        paused: true,
        appData: {
          peerId: userId,
          share: false,
        },
      });
      logger.debug(
        `startRemoteAudio userId=${userId} consume result=${JSON.stringify(
          consumerInfo
        )}`
      );
      //开始消费
      const { id, producerId, kind, producerPaused, rtpParameters, type } =
        consumerInfo;
      try {
        const consumer = await this._recvTransports.get(transportId).consume({
          id,
          recvTransportId: transportId,
          producerId,
          kind,
          rtpParameters,
          //codecOptions, TODO make sure this is redundant
          appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
        });
        logger.debug(
          `startRemoteAudio userId=${userId} consumer created with transportId=${consumer.recvTransportId}`
        );
        // Store in the map.
        this._consumers.set(consumer.id, consumer);

        consumer.on("transportclose", () => {
          logger.debug(
            `startRemoteAudio userId=${userId} transportclose`
          );
          this._consumers.delete(consumer.id);
        });

        const stream = new MediaStream();
        stream.addTrack(consumer.track);
        (qrtcAudio as any).srcObject = stream;
        (qrtcAudio as any)
          .play()
          .catch((error: any) =>
            logger.debug("audioElem.play() failed:" + error)
          );
        //如果服务器pause住关键帧,则要发送resume
        await this._protoo.request("resumeConsumer", {
          transportId: transportId,
          transportType: "webrtc",
          producerId: needConsumeInfo.producerId,
          consumerId: consumer.id,
        });
      } catch (error) {
        logger.debug(`"newConsumer" request failed:${error}`);
        throw error;
      }
    }
  }

  //暂停/恢复接收指定的远端视频流
  async muteRemoteVideo(userId: string, mute: boolean) {
    //找到对应的consumer
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share == undefined
      ) {
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      if (mute) {
        await this._pauseConsumer(consumer);
      } else {
        await this._resumeConsumer(consumer);
      }
    }
  }

  //暂停/恢复接收所有的远端视频流
  async muteAllRemoteViews(mute: boolean) {
    let that = this;
    let muteAllResult = await this._protoo.request(
      mute ? "unViewAll" : "viewAll",
      {}
    );
    logger.debug(
      `muteAllRemoteViews result: ${JSON.stringify(muteAllResult)}`
    );
    //TODO 对于远程流 本地以后不需要再做对应操作了,服务器端会做操作
    // for (var consumerInfo of this._consumers.values()) {
    // 	if(consumerInfo.kind == "video"&& consumerInfo.appData.share == undefined){
    // 		if (mute) {
    // 			consumerInfo.pause()
    // 		} else {
    // 			consumerInfo.resume()
    // 		}
    // 	}

    // }
  }

  //暂停/恢复接收所有的远端音频
  async muteAllRemoteAudios(mute: boolean) {
    let that = this;
    let muteAllResult = await this._protoo.request(
      mute ? "muteAll" : "unMuteAll",
      {}
    );
    logger.debug(
      `muteAllRemoteAudios result: ${JSON.stringify(
        muteAllResult
      )}`
    );
    // for (var consumerInfo of this._consumers.values()) {
    // 	if(consumerInfo.kind == "audio"){
    // 		if (mute) {
    // 			consumerInfo.pause()
    // 		} else {
    // 			consumerInfo.resume()
    // 		}
    // 	}
    // }
  }

  //静音/取消静音指定的远端用户的声音
  async muteRemoteAudio(userId: string, mute: boolean) {
    //找到对应的consumer
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "audio"
      ) {
        consumer = consumerValue;
        logger.debug(
          `muteRemoteAudio consumeInfo=${JSON.stringify(consumer)}`
        );
        break;
      }
    }
    if (consumer) {
      if (mute) {
        await this._pauseConsumer(consumer);
      } else {
        await this._resumeConsumer(consumer);
      }
    }
  }

  //开启大小画面双路编码模式 返回0成功 返回-1表明大画面已经是最低画质了
  enableEncSmallVideoStream(enable: boolean): number {
    //smallVideoEncParam: QRTCVideoEncParams){
    if (enable) {
      this._enableWebcamLayers = true;
      //TODO 如果摄像头已经打开了,关闭摄像头再重新打开 这里不用动态改 开始设置就行
      let resInfo = this.getResolutionInfo(this._encoderParam.videoResolution);
      if (resInfo.canScaleDown) {
        return 0;
      }
      return -1;
    } else {
      this._enableWebcamLayers = false;
      //TODO 如果摄像头已经打开了,关闭摄像头再重新打开
    }
    return -1;
  }

  //选定观看指定userId的大画面或小画面,如果对应的uid没有开启大小画面，则该操作没有任何反应
  async setRemoteVideoStreamType(userId: string, type: QRTCVideoStreamType) {
    //检索userId的视频是否存在 从needConsume里面找，对了needConsume里面的信息，如果对应的人停止了推流，就要删除掉  重要!!!!!
    //100doc的协议不一样，100doc的协议里面因为还没有consume所以并没有consumerId,因此，需要从_consumers里面找
    logger.debug(`setRemoteVideoStreamType userId=${userId}`);
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share == undefined
      ) {
        consumer = consumerValue;
        logger.debug(
          `setRemoteVideoStreamType consumeInfo=${JSON.stringify(
            consumer
          )}`
        );
        break;
      }
    }

    if (consumer) {
      try {
        let layInfo = {
          transportId: consumer.recvTransportId,
          transportType: "webrtc",
          producerId: consumer.producerId,
          consumerId: consumer.id,
          preferredLayers: {
            spatialLayer:
              type == QRTCVideoStreamType.QRTCVideoStreamTypeSmall ? 0 : 1,
          },
        };
        logger.debug(`layerInfo=${JSON.stringify(layInfo)}`);
        const setRemoteVideoStreamTypeResult = await this._protoo.request(
          "setPreferredLayers",
          {
            transportId: consumer.recvTransportId,
            transportType: "webrtc",
            producerId: consumer.producerId,
            consumerId: consumer.id,
            preferredLayers: {
              spatialLayer:
                type == QRTCVideoStreamType.QRTCVideoStreamTypeSmall ? 0 : 1,
            },
          }
        );
        logger.debug(
          `setRemoteVideoStreamTypeResult=${setRemoteVideoStreamTypeResult}`
        );
      } catch (error) {
        logger.debug(`setConsumerPreferredLayers() | failed:${error}`);
      }
    }
  }

  //屏幕分享
  //参数
  //view	渲染控件所在的父控件，可以设置为 nil，表示不显示屏幕分享的预览效果。
  //encParam	屏幕分享的画面编码参数，可以设置为 nil，表示让 SDK 选择最佳的编码参数（分辨率、码率等）。
  async startScreenCapture(
    encParam: QRTCVideoEncParams
  ) {
    this.enableShare(encParam);
  }

    //屏幕分享 electron版本
    async startScreenCaptureForElectron(shareStream: MediaStream){

      if (this._shareProducer) return;
      if (!this._mediasoupDevice.canProduce("video")) {
        return;
      }
  
      let track = shareStream.getVideoTracks()[0];
  
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };
  
      if (this._forceH264) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
          (c) => c.mimeType.toLowerCase() === "video/h264"
        );
  
        if (!codec) {
          throw new Error("desired H264 codec+configuration is not supported");
        }
      }
  
      let encodings = [
        { maxBitrate: this._encoderParam.maxVideoBitrate * 1000 },
      ];
  
      this._shareProducer = await this._sendTransport.produce({
        track,
        encodings,
        codecOptions,
        codec,
        appData: {
          peerName: this._roomInfo.userName,
          peerId: this._roomInfo.userId,
          share: true,
        },
      });
  
      this.safeEmit("screenCaptureStarted");
  
  
      this._shareProducer.on("transportclose", () => {
        this._shareProducer = null;
      });
  
      this._shareProducer.on("trackended", async () => {
        await this.disableShare().catch(() => { });
      });
    }

  //停止屏幕分享
  async stopScreenCapture() {
    await this.disableShare();
  }

  //停止Electron的屏幕共享
  async stopScreenCaptureForElectron() {
    await this.disableShare();
  }

  //暂停屏幕分享
  async pauseScreenCapture() {
    this._shareProducer.pause();
    try {
      await this._protoo.request("pauseProducer", {
        producerId: this._shareProducer.id,
      });
    } catch (error) {
      logger.debug(`pauseScreenCapture() | failed: ${error}`);
    }
    this.safeEmit("screenCapturePaused");
  }

  //继续屏幕分享
  async resumeScreenCapture() {
    logger.debug("resumeScreenCapture()");

    this._shareProducer.resume();

    try {
      await this._protoo.request("resumeProducer", {
        transportId: this._sendTransport.id,
        transportType: "webrtc",
        producerId: this._shareProducer.id,
      });
    } catch (error) {
      logger.debug(`resumeScreenCapture() | failed: ${error}`);
    }
    this.safeEmit("screenCaptureResumed");
  }

  //主动拉远端peer的桌面共享流
  async startRemoteShare(userId: string, qrtcView: HTMLVideoElement) {
    //两种情况 一种是这个视频已经消费过了，只是被paused住了,因此只需要给服务器发送resume即可
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share != undefined
      ) {
        consumer = consumerValue;
        break;
      }
    }
    if (consumer) {
      //发送消息resume该视频
      await this._resumeConsumer(consumer);
      const stream = new MediaStream();
      stream.addTrack(consumer.track);
      this._remoteVideoDoms.set(userId, qrtcView);
      (qrtcView as any).srcObject = stream;
      (qrtcView as any)
        .play()
        .catch((error: any) =>
          logger.debug("videoElem.play() failed:" + error)
        );
      return;
    }
    //另一种情况是没有消费,因此需要进行消费
    var needConsumeInfo: NeedConsumeInfo = null;
    for (const needConsumerValue of this._needConsumes.values()) {
      if (
        userId == needConsumerValue.peerId &&
        needConsumerValue.kind == "video" &&
        needConsumerValue.appData.share != undefined
      ) {
        needConsumeInfo = needConsumerValue;
        logger.debug(
          `startRemoteShare needConsumeInfo=${JSON.stringify(
            needConsumeInfo
          )}`
        );
        break;
      }
    }
    if (needConsumeInfo) {
      let peerId = needConsumeInfo.peerId;
      let transportId = await this._createRecvTransport(
        needConsumeInfo.producerId
      );
      //发送消费请求
      logger.debug(
        `createRecvTransport transportId=${JSON.stringify(
          transportId
        )}`
      );
      const consumerInfo = await this._protoo.request("consume", {
        transportId: transportId,
        transportType: "webrtc",
        producerId: needConsumeInfo.producerId,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        appData: { ...needConsumeInfo.appData, peerId },
      });
      logger.debug(
        `consume result=${JSON.stringify(consumerInfo)}`
      );
      //开始消费
      const { id, producerId, kind, producerPaused, rtpParameters, type } =
        consumerInfo;
      try {
        const consumer = await this._recvTransports.get(transportId).consume({
          id,
          recvTransportId: transportId,
          producerId,
          kind,
          rtpParameters,
          //codecOptions, TODO make sure this is redundant
          appData: { ...needConsumeInfo.appData, peerId: userId }, // Trick.
        });

        // Store in the map.
        this._consumers.set(consumer.id, consumer);

        consumer.on("transportclose", () => {
          this._consumers.delete(consumer.id);
        });

        //如果服务器pause住关键帧,则要发送resume
        if (producerPaused) {
          await this._protoo.request("resumeConsumer", {
            transportId: transportId,
            transportType: "webrtc",
            producerId: needConsumeInfo.producerId,
            consumerId: consumer.id,
          });
        }

        const stream = new MediaStream();
        stream.addTrack(consumer.track);
        (qrtcView as any).srcObject = null;
        (qrtcView as any).srcObject = stream;
        (qrtcView as any)
          .play()
          .catch((error: any) =>
            logger.debug("videoElem.play() failed:" + error)
          );
      } catch (error) {
        logger.debug(`"newConsumer" request failed:${error}`);
        throw error;
      }
    }
  }

  //停止拉取远端peer的桌面共享
  async stopRemoteShare(userId: string) {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumer.appData.peerId &&
        consumer.kind == "video" &&
        consumer.appData.share != undefined
      ) {
        consumer = consumerValue;
        logger.debug(
          `stopRemoteShare consumeInfo=${JSON.stringify(consumer)}`
        );
        break;
      }
    }
    if (consumer) {
      consumer.close();
      this._consumers.delete(consumer.id);
    }
  }

  //获取摄像头列表
  async getCameraDeviceList() {
    let deviceList = await navigator.mediaDevices.enumerateDevices();

    let camDevices: QRTCMediaDeviceInfo[] = [];
    for (let i = 0; i !== deviceList.length; ++i) {
      const deviceInfo = deviceList[i];
      //option.value = deviceInfo.deviceId;
      if (deviceInfo.kind === "videoinput") {
        let device: QRTCMediaDeviceInfo = {
          type: QRTCMediaDeviceType.QRTCMediaDeviceTypeVideoCamera,
          deviceId: deviceInfo.deviceId,
          deviceName: deviceInfo.label || `camera ${i + 1}`,
        };
        camDevices.push(device);
      }
    }
    logger.debug(
      `getCameraDeviceList:${JSON.stringify(camDevices)}`
    );
    if (camDevices.length === 0) {
      this.safeEmit("warning", { code: 1111 });
    }
    return camDevices;
  }

  async getCurrentCameraDevice() {
    return this._webcam.device.deviceId;
  }

  //切换完之后,需要渲染到原来的窗口,此时上层需要调用startlocalpreview
  async setCurrentCameraDevice(deviceId: string) {
    try {
      // Reset video resolution to HD.
      this._webcam.resolution = "hd";
      // Closing the current video track before asking for a new one (mobiles do not like
      // having both front/back cameras open at the same time).
      this._webcamProducer.track.stop();

      logger.debug("changeWebcam() | calling getUserMedia()");

      let resolutionInfo = this.getResolutionInfo(
        this._encoderParam.videoResolution
      );
      let fps = this._encoderParam.videoFps;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { ideal: deviceId },
          width: { ideal: resolutionInfo.resWidth },
          height: { ideal: resolutionInfo.resHeight },
          frameRate: fps,
          //...(VIDEO_CONSTRAINS as any)[resolution]
        },
      });

      const track = stream.getVideoTracks()[0];
      this._currentCameraDeviceId = deviceId;
      await this._webcamProducer.replaceTrack({ track });
      //此处不该直接渲染 而是交给上层去选择渲染哪些窗口
      // if(this._localQRTCView){
      // 	const streamPlay = new MediaStream;
      // 	streamPlay.addTrack(this._webcamProducer.track);

      // 	(this._localQRTCView as any).srcObject = streamPlay;
      // 	(this._localQRTCView as any).play().catch((error: any) => logger.debug('videoElem.play() failed:' + error));
      // }
    } catch (error) {
      logger.debug(`changeWebcam() | failed: ${error}`);
    }
  }

  //获取麦克风列表
  async getMicDevicesList() {
    let deviceList = await navigator.mediaDevices.enumerateDevices();

    let micDevices: QRTCMediaDeviceInfo[] = [];
    for (let i = 0; i !== deviceList.length; ++i) {
      const deviceInfo = deviceList[i];
      //option.value = deviceInfo.deviceId;
      if (deviceInfo.kind === "audioinput") {
        if (
          deviceInfo.deviceId !== "communications" &&
          deviceInfo.deviceId !== "default"
        ) {
          let device: QRTCMediaDeviceInfo = {
            type: QRTCMediaDeviceType.QRTCMediaDeviceTypeAudioInput,
            deviceId: deviceInfo.deviceId,
            deviceName: deviceInfo.label || `microphone ${i + 1}`,
          };
          micDevices.push(device);
        }
      }
    }
    logger.debug(
      `getMicDeviceList:${JSON.stringify(micDevices)}`
    );
    if (micDevices.length === 0) {
      this.safeEmit("warning", { code: 1201 });
    }
    return micDevices;
  }

  async setCurrentMic(deviceId: string) {
    this._micProducer.track.stop();

    try {
      logger.debug("enableMic() | calling getUserMedia()");
      const constraints = {
        audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const track = stream.getAudioTracks()[0];

      await this._micProducer.replaceTrack({ track });
      this._currentMicDeviceId = deviceId;
    } catch (error) {
      logger.debug(`setCurrentMic() | failed: ${error}`);
    }
  }

  //获取扬声器列表
  async getSpeakerDevicesList() {
    let deviceList = await navigator.mediaDevices.enumerateDevices();

    let speakerDevices: QRTCMediaDeviceInfo[] = [];
    for (let i = 0; i !== deviceList.length; ++i) {
      const deviceInfo = deviceList[i];
      //option.value = deviceInfo.deviceId;
      if (deviceInfo.kind === "audiooutput") {
        if (
          deviceInfo.deviceId !== "communications" &&
          deviceInfo.deviceId !== "default"
        ) {
          let device: QRTCMediaDeviceInfo = {
            type: QRTCMediaDeviceType.QRTCMediaDeviceTypeAudioOutput,
            deviceId: deviceInfo.deviceId,
            deviceName: deviceInfo.label || `speaker ${i + 1}`,
          };
          speakerDevices.push(device);
        }
      }
    }
    logger.debug(
      `getSpeakerDeviceList:${JSON.stringify(speakerDevices)}`
    );
    return speakerDevices;
  }

  //需要将扬声器id输出到audio标签 elements:是audio标签的数组 [audio]
  async setCurrentSpeaker(deviceId: string, elements: any = []) {
    //for转换遍历elements
    this._currentSpeakerDeviceId = deviceId;
    for (const element of elements) {
      if (typeof element.sinkId !== "undefined") {
        element
          .setSinkId(deviceId)
          .then(() => {
            logger.debug(`Success, audio output device attached: ${deviceId}`);
          })
          .catch((error: any) => {
            let errorMessage = error;
            if (error.name === "SecurityError") {
              errorMessage = `You need to use HTTPS for selecting audio output device: ${error}`;
            }
            console.error(errorMessage);
            // Jump back to first output device in the list as it's the default.
            //audioOutputSelect.selectedIndex = 0;
          });
      } else {
        console.warn("Browser does not support output device selection.");
      }
    }
  }

  getCurrentCamera():string|undefined {
		if (!this._webcamProducer) {
			return undefined;
		}
		return this._currentCameraDeviceId;
	}

	getCurrentMic():string|undefined {
		if (!this._micProducer) {
			return undefined;
		}
		return this._currentMicDeviceId;
	}

	getCurrentSpeaker():string|undefined {
		return this._currentSpeakerDeviceId;
	}

  // 获取本地用户的音频信息
  async getLocalUserAudioStats() {
    if (this._webcamProducer) {
      return this._micProducer.getStats();
    } else {
      return null;
    }
  }

  // 获取本地用户的视频信息
  async getLocalUserVideoStats() {
    if (this._micProducer) {
      return this._webcamProducer.getStats();
    } else {
      return null;
    }
  }

  //获取本地用户的共享信息
  async getLocalUserShareStats() {
    if (this._shareProducer) {
      return this._shareProducer.getStats();
    } else {
      return null;
    }
  }
  //获取对应远程用户的视频 音频流的状态统计信息
  async getRemoteUserAudioStats(userId: string) {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "audio"
      ) {
        consumer = consumerValue;
        break;
        //logger.debug(`getRemoteUserAudioStats consumeInfo=${JSON.stringify(consumer)}`)
      }
    }
    if (consumer) {
      return consumer.getStats();
    } else {
      return null;
    }
  }

  //获取对应远程用户的视频信息
  async getRemoteUserVideoStats(userId: string) {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share == undefined
      ) {
        consumer = consumerValue;
        break;
        //logger.debug(`getRemoteUserAudioStats consumeInfo=${JSON.stringify(consumer)}`)
      }
    }
    if (consumer) {
      return consumer.getStats();
    } else {
      return null;
    }
  }
  //获取对应远程用户的共享信息
  async getRemoteUserShareStats(userId: string) {
    var consumer: Consumer = null;
    for (const consumerValue of this._consumers.values()) {
      if (
        userId == consumerValue.appData.peerId &&
        consumerValue.kind == "video" &&
        consumerValue.appData.share != undefined
      ) {
        consumer = consumerValue;
        break;
        //logger.debug(`getRemoteUserAudioStats consumeInfo=${JSON.stringify(consumer)}`)
      }
    }
    if (consumer) {
      return consumer.getStats();
    } else {
      return null;
    }
  }

  async onFlyBandWidth() {
    let parameters = this._webcamProducer.rtpSender.getParameters();
    parameters.encodings[0].maxBitrate = 125 * 1000;
    this._webcamProducer.rtpSender.setParameters(parameters);
  }

  // 获取采集音量值
  getAudioCaptureVolume() {
    return this._audioVolumn;
  }

  // 设置采集音量
  setAudioCaptureVolume(volume: number) {
    if (volume < 0 || volume > 100) {
      return;
    }

    this._audioVolumn = volume;

    this._gainNode.gain.value = 0.01 * volume;
  }

  // 获取播出声音
  getAudioPlayoutVolume() {
    return this._audioPlayoutVolumn;
  }

  // 设置播出音量
  setAudioPlayoutVolume(elements: any, volume: number) {
    elements.forEach((element: any) => {
      element.volume = 0.01 * volume;
    });
  }

  // 设置远端图像的渲染模式
  setRemoteViewFillMode(userId: string, mode: QRTCVideoFillMode) {
    let qrtcView = this._remoteVideoDoms.get(userId);
    if (qrtcView) {
      (qrtcView as any).style["object-fit"] =
        mode == QRTCVideoFillMode.QRTCVideoFillMode_Fill ? "fill" : "contain";
    }
  }

  // 获取版本号
  getSDKVersion() {
    return "0.5.0.0";
  }

  //开启MCU
  async beginMcu() {
    const ret = await this._protoo.request("mcuStart", {
      appID: "0",
      roomID: this._roomInfo.roomId,
      peerID: this._roomInfo.userId,
      sessionID: "",
    });
    const { sessionId, message } = ret;
    this._mcuSessionId = sessionId;
    logger.debug(`beginMcu ret:${JSON.stringify(ret)}`);
  }

  //结束MCU
  async endMcu() {
    const ret = await this._protoo.request("mcuEnd", {
      appID: "0",
      roomID: this._roomInfo.roomId,
      peerID: this._roomInfo.userId,
      sessionID: this._mcuSessionId,
    });
    const { message } = ret;
    logger.debug(`endMcu ret:${JSON.stringify(ret)}`);
  }

  async testMcuLayout() {
    let userIdList = [];
    userIdList.push(this._roomInfo.userId);
    for (let [key, value] of this._peers) {
      userIdList.push(key);
    }
    await this.setMcuLayout(true, userIdList as any, "2x2");
  }
  //设置MCU Layout
  async setMcuLayout(
    bInit: boolean,
    userIdList: [string],
    layoutPreset: string
  ) {
    //layoutpreset 2x2 3x3 4x4 ....
    //parse layout preset
    let denominator = 2;
    switch (layoutPreset) {
      case "2x2":
        denominator = 2;
        break;
      case "3x3":
        denominator = 3;
        break;
      case "4x4":
        denominator = 4;
        break;
      case "4x4":
        denominator = 5;
        break;
    }

    let peersInfo: any = {
      video: [],
      audio: [],
    };
    let video = [];
    let audio = [];
    let layoutIndex = 0;
    //parse userlist
    for (const userId of userIdList) {
      //如果是自己，就单独加进去
      if (userId == this._roomInfo.userId) {
        let videoInfo = {
          displayName:
            this._roomInfo.userName != undefined
              ? this._roomInfo.userName
              : "noName",
          peerId: userId,
          producerId:
            this._webcamProducer != null ? this._webcamProducer.id : "",
          peerLogo: "https://www.100doc.com.cn/novideo.jpeg",
          region: {
            shape: "rectangle",
            area: {
              left: {
                numerator: layoutIndex % denominator, //Math.floor(layoutIndex / denominator) + layoutIndex % denominator,
                denominator,
              },
              top: {
                numerator: Math.floor(layoutIndex / denominator),
                denominator,
              },
              width: {
                numerator: 1, //先一样大小,后面再考虑异形布局
                denominator,
              },
              height: {
                numerator: 1,
                denominator,
              },
            },
          },
        };
        video.push(videoInfo);
        layoutIndex++;
        continue;
      }
      //获取用户对应的音视频信息
      let userInfo = this._peers.get(userId);

      for (const consumerInfo of this._consumers.values()) {
        if (
          userId == consumerInfo.appData.peerId &&
          consumerInfo.appData.share == undefined
        ) {
          if (consumerInfo.kind == "video") {
            let videoInfo = {
              displayName:
                userInfo.displayName != undefined
                  ? userInfo.displayName
                  : "noName",
              peerId: userId,
              producerId: consumerInfo.producerId,
              peerLogo: "https://www.100doc.com.cn/novideo.jpeg",
              region: {
                shape: "rectangle",
                area: {
                  left: {
                    numerator: layoutIndex % denominator, //Math.floor(layoutIndex / denominator) + layoutIndex % denominator,
                    denominator,
                  },
                  top: {
                    numerator: Math.floor(layoutIndex / denominator),
                    denominator,
                  },
                  width: {
                    numerator: 1, //先一样大小,后面再考虑异形布局
                    denominator,
                  },
                  height: {
                    numerator: 1,
                    denominator,
                  },
                },
              },
            };
            video.push(videoInfo);
          } else if (consumerInfo.kind == "audio") {
            let audioInfo = {
              peerId: userId,
              producerId: consumerInfo.producerId,
            };
            audio.push(audioInfo);
          }
        }
      }
      layoutIndex++;
    }
    peersInfo.video = video;
    peersInfo.audio = audio;

    let mcuinfo = {
      appID: "0",
      roomID: this._roomInfo.roomId,
      peerID: this._roomInfo.userId,
      sessionID: this._mcuSessionId,
      layerSize: {
        width: 720,
        height: 1080,
      },
      peersInfo,
    };
    logger.debug(`mcuinfo=${JSON.stringify(mcuinfo)}`);
    const ret = await this._protoo.request("mcuLayout", {
      appId: "0",
      roomID: this._roomInfo.roomId,
      peerID: this._roomInfo.userId,
      sessionID: this._mcuSessionId,
      action: bInit ? "init" : "update",
      backgroundPicture: "https://www.100doc.com.cn/bgp.jpeg",
      backgroundColor: {
        r: 0,
        g: 0,
        b: 0,
      },
      defaultLogo: "https://www.100doc.com.cn/g_novideo.jpeg",
      audioModel: 0,
      layerSize: {
        width: 1280,
        height: 720,
      },
      peersInfo,
    });
  }
  //###########################################SDK接口结束###########################################

  //#################################私有函数开始 SDK内部调用#############################################
  //创建发送transport
  async _createSendTransport() {
    if (!this._sendTransport) {
      //创建
      logger.debug(`createWebRtcTransport`);
      const transportInfo = await this._protoo.request(
        "createWebRtcTransport",
        {
          direction: "sendonly",
          forceTcp: this._forceTcp,
          workerId: 0,
          appData: {
            producing: true,
            consuming: false,
          },
        }
      );

      logger.debug(
        `createWebRtcTransport ret:${JSON.stringify(
          transportInfo
        )}`
      );

      const {
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
        sctpParameters,
      } = transportInfo;

      this._sendTransport = this._mediasoupDevice.createSendTransport({
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
        sctpParameters,
        iceServers: [],
        proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
      });

      this._sendTransport.on(
        "connect",
        (
          { dtlsParameters },
          callback,
          errback // eslint-disable-line no-shadow
        ) => {
          this._protoo
            .request("connectWebRtcTransport", {
              transportId: this._sendTransport.id,
              dtlsParameters,
            })
            .then(callback)
            .catch(errback);
        }
      );

      this._sendTransport.on(
        "produce",
        async ({ kind, rtpParameters, appData }, callback, errback) => {
          try {
            // eslint-disable-next-line no-shadow
            const { id } = await this._protoo.request("produce", {
              transportId: this._sendTransport.id,
              kind,
              rtpParameters,
              paused: false,
              appData,
            });

            callback({ id });
          } catch (error) {
            errback(error as Error);
          }
        }
      );

      this._sendTransport.on("connectionstatechange", (connectionState) => {
        logger.debug(
          `_sendTransport on connectionstatechange=${connectionState}`
        );
        if (connectionState == "disconnected") {
          //等待2秒,如果没有重连上,就需要restart
          var that = this;
          const operation = retry.operation({
            retries: 3,
            factor: 2,
            minTimeout: 1 * 1000, //3秒后看一下
            maxTimeout: 2 * 1000, //后面每隔2秒试一下
          });
          operation.attempt((currentAttempt) => {
            if (!that._sendTransport) {
              operation.stop();
              return;
            }
            if (that._sendTransport.closed) {
              operation.stop();
              return;
            }
            if (that._sendTransport.connectionState !== "connected") {
              logger.debug(
                `Attempt restart sendTransport failed for ${currentAttempt}`
              );
              //that.restartIce();
              if (operation.retry()) return;
            } else {
              logger.debug(
                ` restartIce success after ${currentAttempt} retry times`
              );
              operation.stop();
              return;
            }
          });
        }
      });
    }
  }

  async _createRecvTransport(producerId: string): Promise<string> {
    const transportInfo = await this._protoo.request("createWebRtcTransport", {
      direction: "recvonly",
      forceTcp: this._forceTcp,
      workerId: 0,
      appData: {
        producing: false,
        consuming: true,
        producerId: producerId,
      },
    });

    const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } =
      transportInfo;
    logger.debug(
      `createRecvTransport result=${JSON.stringify(transportInfo)}`
    );
    var that = this;
      //先查找有没有这个Transport
      let transportExist: boolean = false;
      for(let recvTransport of that._recvTransports.values()){
        if(recvTransport.id == id){
          transportExist = true;
          break;
        }
      }

      // that._recvTransports.forEach(function (value, key, map) {
      //   //value和key就是map的key，value，map是_needConsumes本身
      //   if (id == value.id) {
      //     transportExist = true;
      //   }
      // });
      if (transportExist) {
        logger.debug(`recvTransport already Exist`);
        return id;
        //return id
      } else {
        logger.debug(`recvTransport not Exist,creating...`);
        const recvTransport: Transport =
          that._mediasoupDevice.createRecvTransport({
            id,
            iceParameters,
            iceCandidates,
            dtlsParameters,
            sctpParameters,
            iceServers: [],
          });

        recvTransport.on(
          "connect",
          (
            { dtlsParameters },
            callback,
            errback // eslint-disable-line no-shadow
          ) => {
            that._protoo
              .request("connectWebRtcTransport", {
                transportId: recvTransport.id,
                dtlsParameters,
              })
              .then(callback)
              .catch(errback);
          }
        );

        that._recvTransports.set(id, recvTransport);
        return id;
        //return id
      }
  }

  //创建接收transport
  // async _createRecvTransport(producerId: string): Promise<string> {
  //   return new Promise(async (resolve, reject) => {
  //   const transportInfo = await this._protoo.request("createWebRtcTransport", {
  //     direction: "recvonly",
  //     forceTcp: this._forceTcp,
  //     workerId: 0,
  //     appData: {
  //       producing: false,
  //       consuming: true,
  //       producerId: producerId,
  //     },
  //   });

  //   const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } =
  //     transportInfo;
  //   logger.debug(
  //     `createRecvTransport result=${JSON.stringify(transportInfo)}`
  //   );
  //   var that = this;
  //     //先查找有没有这个Transport
  //     let transportExist: boolean = false;
  //     for(let recvTransport of that._recvTransports.values()){
  //       if(recvTransport.id == id){
  //         transportExist = true;
  //         break;
  //       }
  //     }

  //     // that._recvTransports.forEach(function (value, key, map) {
  //     //   //value和key就是map的key，value，map是_needConsumes本身
  //     //   if (id == value.id) {
  //     //     transportExist = true;
  //     //   }
  //     // });
  //     if (transportExist) {
  //       that.reportLog(`recvTransport already Exist`);
  //       resolve(id);
  //       //return id
  //     } else {
  //       that.reportLog(`recvTransport not Exist,creating...`);
  //       const recvTransport: Transport =
  //         that._mediasoupDevice.createRecvTransport({
  //           id,
  //           iceParameters,
  //           iceCandidates,
  //           dtlsParameters,
  //           sctpParameters,
  //           iceServers: [],
  //         });

  //       recvTransport.on(
  //         "connect",
  //         (
  //           { dtlsParameters },
  //           callback,
  //           errback // eslint-disable-line no-shadow
  //         ) => {
  //           that._protoo
  //             .request("connectWebRtcTransport", {
  //               transportId: recvTransport.id,
  //               dtlsParameters,
  //             })
  //             .then(callback)
  //             .catch(errback);
  //         }
  //       );

  //       that._recvTransports.set(id, recvTransport);
  //       resolve(id);
  //       //return id
  //     }

  //   //return p;
  // });
  // }

  async enableMic(mute?:boolean) {
    logger.debug("enableMic()");

    if (this._micProducer) return;

    if (!this._mediasoupDevice.canProduce("audio")) {
      logger.debug("enableMic() | cannot produce audio");
      return;
    }

    // if (this._audioContext == null) {
    // 	this._audioContext = new AudioContext();
    // 	this._script = this._audioContext.createScriptProcessor(2048, 1, 1);
    // 	var that = this;
    // 	var started = false;
    // 	this._script.addEventListener("audioprocess",(event:AudioProcessingEvent)=>{
    // 		const input = event.inputBuffer.getChannelData(0);
    // 		let i;
    // 		let sum = 0.0;
    // 		for (i = 0; i < input.length; ++i) {
    // 		  sum += input[i] * input[i];
    // 		}
    // 		let instance = Math.sqrt(sum / input.length);
    // 		let instanceInt = Math.round(instance*100);//四舍五入
    // 		if(!that._audioLocalMute){
    // 			if(instanceInt > 15){
    // 				//发送voiceEnergy action为start
    // 				//that.safeEmit('instantInputVolume',{volume: instance*100})
    // 				//TODO 此处不能await 会阻塞声音
    // 				if(!started){
    // 					that._protoo.request(
    // 						'voiceEnergy', {
    // 							peerId: this._roomInfo.userId,
    // 							action: 'start',
    // 							voice: instanceInt>100? 100:instanceInt
    // 					});
    // 					//自己说话也要提示
    // 					this.safeEmit('userSpeaking',{
    // 						userId: this._roomInfo.userId,
    // 						action: "start"
    // 					  });
    // 					//如果一直说,则会中断timeout 一旦timeout执行下去，就是没有被中断 4秒后没说话,就发end
    // 					that._voiceTimeout = setTimeout(() => {
    // 						that._protoo.request(
    // 							'voiceEnergy', {
    // 							peerId: this._roomInfo.userId,
    // 							action: 'end',
    // 						});
    // 						started = false;
    // 					}, 4000);
    // 					started = true;
    // 				}else{
    // 					//重新settimeout
    // 					clearTimeout(that._voiceTimeout);
    // 					that._voiceTimeout = setTimeout(() => {
    // 						that._protoo.request(
    // 							'voiceEnergy', {
    // 							peerId: this._roomInfo.userId,
    // 							action: 'end',
    // 						});
    // 						started =false;
    // 					}, 4000);
    // 				}
    // 			}
    // 		}
    // 		//还要再把数据传下去
    // 		//输入流位置
    // 		var inputBuffer = event.inputBuffer;
    // 		//输出流位置
    // 		var outputBuffer = event.outputBuffer;
    // 		//遍历通道处理数据
    // 		for (var channel = 0; channel < outputBuffer.numberOfChannels; channel++) {
    // 			var inputData = inputBuffer.getChannelData(channel);
    // 			var outputData = outputBuffer.getChannelData(channel);
    // 				for (let i = 0; i < inputData.length; i = i + 1) {
    // 					//直接将输入的数据传给输出通道
    // 					outputData[i] = inputData[i];
    // 				}
    // 		};
    // 	})
    // 	this._gainNode = this._audioContext.createGain();
    // }

    let track;

    try {
      logger.debug("enableMic() | calling getUserMedia()");
      var stream: any = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error: any) {
        if (error.name === "NotAllowedError") {
          this.safeEmit("error", { code: -1317 });
        } else if (error.name === "AbortError") {
          this.safeEmit("error", { code: -1302 });
        } else if (error.name === "OverConstrainedError") {
          this.safeEmit("error", { code: -1318 });
        } else if (error.name === "NotReadableError") {
          this.safeEmit("error", { code: -131 });
        }
      }
      // if(!this._audioSource&&!this._audioDestination){
      // 	this._audioSource = this._audioContext.createMediaStreamSource(stream);
      // 	this._audioDestination = this._audioContext.createMediaStreamDestination();
      // }
      // this._audioSource.connect(this._gainNode);
      // this._gainNode.connect(this._script)
      // this._script.connect(this._audioDestination);

      // this._gainNode.gain.value = 1;
      // this._audioVolumn = 100;
      // this._audioPlayoutVolumn = 100
      // this._audioStreamForGain = this._audioDestination.stream;
      //audioElement.srcObject = window.localStream; //for playback
      //you can add this stream to pc object
      // pc.addStream(window.localStream);
      track = stream.getAudioTracks()[0];

      this._micProducer = await this._sendTransport.produce({
        track,
        codecOptions: {
          opusStereo: false,
          opusDtx: true,
          opusMaxPlaybackRate: 16000,
        },
        appData: {
          peerName: this._roomInfo.userName,
          peerId: this._roomInfo.userId,
        },
        // NOTE: for testing codec selection.
        // codec : this._mediasoupDevice.rtpCapabilities.codecs
        // 	.find((codec) => codec.mimeType.toLowerCase() === 'audio/pcma')
      });
      let deviceId = track.getSettings().deviceId;
      this._currentMicDeviceId = deviceId;
      logger.debug(`enableMic() | deviceId:${deviceId}`);

      this.safeEmit("localAudioTrack", this._micProducer.track);

      this._micProducer.on("transportclose", () => {
        this._micProducer = null;
      });

      this._micProducer.on("trackended", () => {
        this.disableMic().catch(() => {});
      });
    } catch (error) {
      logger.debug(`enableMic() | failed:${error}`);
      if (track) track.stop();
    }
  }

  async disableMic() {
    logger.debug("disableMic()");

    if (!this._micProducer) return;

    this._micProducer.close();

    try {
      await this._protoo.request("closeProducer", {
        transportId: this._sendTransport.id,
        transportType: "webrtc",
        producerId: this._micProducer.id,
      });
    } catch (error) {}

    this._micProducer = null;
  }

  // 启动本地摄像头
  // async enableWebcam(qrtcView?: HTMLVideoElement) {
  //   logger.debug("enableWebcam()");

  //   if (this._webcamProducer) {
  //     //如果view存在,则直接渲染
  //     if (qrtcView) {
  //       const streamPlay = new MediaStream();
  //       streamPlay.addTrack(this._webcamProducer.track);
  //       (qrtcView as any).srcObject = streamPlay;
  //       (qrtcView as any)
  //         .play()
  //         .catch((error: any) =>
  //           logger.debug("videoElem.play() failed:" + error)
  //         );
  //     }
  //     return;
  //   }

  //   if (!this._mediasoupDevice.canProduce("video")) {
  //     logger.debug("enableWebcam() | cannot produce video");

  //     return;
  //   }

  //   //this._localQRTCView = qrtcView;

  //   let track;
  //   let device;

  //   try {
  //     await this._updateWebcams();
  //     device = this._webcam.device;

  //     //const { resolution } = this._webcam;

  //     if (!device) throw new Error("no webcam devices");

  //     logger.debug("enableWebcam() | calling getUserMedia()");

  //     let resolutionInfo = this.getResolutionInfo(
  //       this._encoderParam.videoResolution
  //     );
  //     let fps = this._encoderParam.videoFps;
  //     var stream: any = null;
  //     try {
  //       stream = await navigator.mediaDevices.getUserMedia({
  //         video: {
  //           deviceId: { ideal: device.deviceId },
  //           width: { ideal: resolutionInfo.resWidth },
  //           height: { ideal: resolutionInfo.resHeight },
  //           frameRate: fps,
  //           //...(VIDEO_CONSTRAINS as any)[resolution]
  //         },
  //       });
  //     } catch (error: any) {
  //       //logger.debug(123456, error.name, error.message)
  //       if (error.name === "NotAllowedError") {
  //         this.safeEmit("error", { code: -1314 });
  //       } else if (error.name === "AbortError") {
  //         this.safeEmit("error", { code: -1301 });
  //       } else if (error.name === "OverConstrainedError") {
  //         this.safeEmit("error", { code: -1315 });
  //       } else if (error.name === "NotReadableError") {
  //         logger.debug("camera NotReadableError");
  //         this.safeEmit("error", { code: -1316 });
  //       }
  //     }

  //     track = stream.getVideoTracks()[0];

  //     let encodings;
  //     let codec;
  //     const codecOptions = {
  //       videoGoogleStartBitrate: 1000,
  //     };

  //     if (this._forceH264) {
  //       codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
  //         (c) => c.mimeType.toLowerCase() === "video/h264"
  //       );

  //       if (!codec) {
  //         throw new Error("desired H264 codec+configuration is not supported");
  //       }
  //     }

  //     //设置了大小流模式并且用户设置的分辨率能支持大小流
  //     if (this._enableWebcamLayers && resolutionInfo.canScaleDown) {
  //       // If VP9 is the only available video codec then use SVC.
  //       const firstVideoCodec =
  //         this._mediasoupDevice.rtpCapabilities.codecs.find(
  //           (c) => c.kind === "video"
  //         );

  //       //let encodings;
  //       let simulcastInfo = [
  //         {
  //           scaleResolutionDownBy: resolutionInfo.scaleDownBy,
  //           maxBitrate: resolutionInfo.maxDownBitrate * 1000,
  //         },
  //         {
  //           scaleResolutionDownBy: 1,
  //           maxBitrate: resolutionInfo.maxUpBitrate * 1000,
  //         },
  //       ];

  //       logger.debug(`firstVideoCodec is not vp9...`);
  //       encodings = simulcastInfo;

  //       this._webcamProducer = await this._sendTransport.produce({
  //         track,
  //         encodings,
  //         codecOptions: {
  //           videoGoogleStartBitrate: 1000,
  //         },
  //         // NOTE: for testing codec selection.
  //         codec: this._mediasoupDevice.rtpCapabilities.codecs.find(
  //           (codec) => codec.mimeType.toLowerCase() === "video/h264"
  //         ),
  //         appData: {
  //           peerName: this._roomInfo.userName,
  //           peerId: this._roomInfo.userId,
  //         },
  //       });
  //     } else {
  //       logger.debug(`else......`);
  //       //TODO 确保这里不是simulcast
  //       let encodeInfo = [
  //         { maxBitrate: this._encoderParam.maxVideoBitrate * 1000 },
  //       ];
  //       encodings = encodeInfo;
  //       this._webcamProducer = await this._sendTransport.produce({
  //         track,
  //         encodings,
  //         codecOptions,
  //         codec,
  //         appData: {
  //           peerName: this._roomInfo.userName,
  //           peerId: this._roomInfo.userId,
  //         },
  //       });
  //     }

  //     if (qrtcView) {
  //       const streamPlay = new MediaStream();
  //       streamPlay.addTrack(this._webcamProducer.track);
  //       (qrtcView as any).srcObject = streamPlay;
  //       (qrtcView as any)
  //         .play()
  //         .catch((error: any) =>
  //           logger.debug("videoElem.play() failed:" + error)
  //         );
  //     }

  //     logger.debug(`emit localVideoTrack event`);

  //     this._webcamProducer.on("transportclose", () => {
  //       logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
  //       this._webcamProducer = null;
  //     });

  //     this._webcamProducer.on("trackended", () => {
  //       logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
  //       this.disableWebcam().catch(() => {});
  //     });
  //   } catch (error) {
  //     logger.debug(`enableWebcam() | failed:${error}`);
  //     if (track) track.stop();
  //   }
  // }

  // 启动本地摄像头
  async enableWebcam(qrtcView?: HTMLVideoElement) {
    logger.debug("enableWebcam()");

    if (this._webcamProducer) {
      //如果view存在,则直接渲染
      if (qrtcView) {
        const streamPlay = new MediaStream();
        streamPlay.addTrack(this._webcamProducer.track);
        (qrtcView as any).srcObject = streamPlay;
        (qrtcView as any)
          .play()
          .catch((error: any) =>
            logger.debug("videoElem.play() failed:" + error)
          );
      }
      return;
    }

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.debug("enableWebcam() | cannot produce video");

      return;
    }

    //this._localQRTCView = qrtcView;

    let track;
    let device;

    try {
      await this._updateWebcams();
      device = this._webcam.device;

      //const { resolution } = this._webcam;

      if (!device) throw new Error("no webcam devices");

      logger.debug("enableWebcam() | calling getUserMedia()");

      let resolutionInfo = this.getResolutionInfo(
        this._encoderParam.videoResolution
      );
      let fps = this._encoderParam.videoFps;
      var stream: any = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { ideal: device.deviceId },
            width: { ideal: resolutionInfo.resWidth },
            height: { ideal: resolutionInfo.resHeight },
            frameRate: fps,
            //...(VIDEO_CONSTRAINS as any)[resolution]
          },
        });
      } catch (error: any) {
        //logger.debug(123456, error.name, error.message)
        if (error.name === "NotAllowedError") {
          this.safeEmit("error", { code: -1314 });
        } else if (error.name === "AbortError") {
          this.safeEmit("error", { code: -1301 });
        } else if (error.name === "OverConstrainedError") {
          this.safeEmit("error", { code: -1315 });
        } else if (error.name === "NotReadableError") {
          logger.debug("camera NotReadableError");
          this.safeEmit("error", { code: -1316 });
        }
      }

      track = stream.getVideoTracks()[0].clone();

      let encodings;
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };


      if (this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_H264Base ||
        this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_H264Main ||
        this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_H264High) {
        // Code for H264 codec
        codec = this._mediasoupDevice.rtpCapabilities.codecs
          .find((c) => c.mimeType.toLowerCase() === 'video/h264');
        if (!codec) {
          throw new Error('desired H264 codec+configuration is not supported');
        }
      }
      else if (this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_VP9) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs
          .find((c) => c.mimeType.toLowerCase() === 'video/vp9');

        if (!codec) {
          throw new Error('desired VP9 codec+configuration is not supported');
        }
      } else if (this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_VP8) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs
          .find((c) => c.mimeType.toLowerCase() === 'video/vp8');

        if (!codec) {
          throw new Error('desired VP8 codec+configuration is not supported');
        }
      }

      // TODO by pcg 20240110
      //设置了大小流模式并且用户设置的分辨率能支持大小流
      if (this._enableWebcamLayers && resolutionInfo.canScaleDown) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec = this._mediasoupDevice
          .rtpCapabilities
          .codecs
          .find((c) => c.kind === 'video');

        // VP9 with SVC.
        if (
          (this._encoderParam.preferCodec === QRTCCodecType.QRTCCodec_VP9 && codec) ||
          firstVideoCodec.mimeType.toLowerCase() === 'video/vp9'
        ) {
          encodings =
            [
              {
                maxBitrate: 5000000,
                scalabilityMode: 'L3T3_KEY' //TODO should support setting by user
              }
            ];
        }
        else {
          // VP8 or H264 with simulcast.
          encodings = [
            {
              scaleResolutionDownBy: resolutionInfo.scaleDownBy,
              maxBitrate: resolutionInfo.maxDownBitrate * 1000,
            },
            {
              scaleResolutionDownBy: 1,
              maxBitrate: resolutionInfo.maxUpBitrate * 1000,
            },
          ];

          logger.debug(`firstVideoCodec is not vp9...`);
        }


        this._webcamProducer = await this._sendTransport.produce({
          track,
          encodings,
          codecOptions,
          // NOTE: for testing codec selection.
          codec,
          appData: {
            peerName: this._roomInfo.userName,
            peerId: this._roomInfo.userId,
          },
        });
      }

      let encodeInfo = [
        { maxBitrate: this._encoderParam.maxVideoBitrate * 1000 },
      ];
      encodings = encodeInfo;
      this._webcamProducer = await this._sendTransport.produce({
        track,
        encodings: this._enableWebcamLayers ? encodings : encodeInfo,
        codecOptions,
        codec,
        appData: {
          peerName: this._roomInfo.userName,
          peerId: this._roomInfo.userId,
        },
      });

      this._currentCameraDeviceId = device.deviceId;

      if (qrtcView) {
        const streamPlay = new MediaStream();
        streamPlay.addTrack(this._webcamProducer.track);
        (qrtcView as any).srcObject = streamPlay;
        (qrtcView as any)
          .play()
          .catch((error: any) =>
            logger.debug("videoElem.play() failed:" + error)
          );
      }

      logger.debug(`emit localVideoTrack event`);

      this._webcamProducer.on("transportclose", () => {
        logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
        this._webcamProducer = null;
      });

      this._webcamProducer.on("trackended", () => {
        logger.debug(`[RoomClientD] _webcamProducer on transportclose`);
        this.disableWebcam().catch(() => { });
      });
    } catch (error) {
      logger.debug(`enableWebcam() | failed:${error}`);
      if (track) track.stop();
    }
  }

  async disableWebcam() {
    logger.debug("disableWebcam()");

    if (!this._webcamProducer) return;
    logger.debug(`[RoomClient111] this._webcamProducer.close`);
    this._webcamProducer.close();

    try {
      await this._protoo.request("closeProducer", {
        transportId: this._sendTransport.id,
        transportType: "webrtc",
        producerId: this._webcamProducer.id,
      });
    } catch (error) {}

    this._webcamProducer = null;
  }

  // async changeWebcam() {
  // 	logger.debug('changeWebcam()');

  // 	try {
  // 		await this._updateWebcams();

  // 		const array = Array.from(this._webcams.keys());
  // 		const len = array.length;
  // 		const deviceId =
  // 			this._webcam.device ? this._webcam.device.deviceId : undefined;
  // 		let idx = array.indexOf(deviceId);

  // 		if (idx < len - 1)
  // 			idx++;
  // 		else
  // 			idx = 0;

  // 		this._webcam.device = this._webcams.get(array[idx]);

  // 		logger.debug(
  // 			'changeWebcam() | new selected webcam [device:%o]',
  // 			this._webcam.device);

  // 		// Reset video resolution to HD.
  // 		this._webcam.resolution = 'hd';

  // 		if (!this._webcam.device)
  // 			throw new Error('no webcam devices');

  // 		// Closing the current video track before asking for a new one (mobiles do not like
  // 		// having both front/back cameras open at the same time).
  // 		this._webcamProducer.track.stop();

  // 		logger.debug('changeWebcam() | calling getUserMedia()');

  // 		let resolutionInfo = this.getResolutionInfo(this._encoderParam.videoResolution);
  // 		let fps = this._encoderParam.videoFps;

  // 		const stream = await navigator.mediaDevices.getUserMedia(
  // 			{
  // 				video:
  // 				{
  // 					deviceId: { ideal: this._webcam.device.deviceId },
  // 					width: { ideal: resolutionInfo.resWidth },
  // 					height: { ideal: resolutionInfo.resHeight },
  // 					frameRate: fps
  // 					//...(VIDEO_CONSTRAINS as any)[resolution]
  // 				}
  // 			});

  // 		const track = stream.getVideoTracks()[0];

  // 		await this._webcamProducer.replaceTrack({ track });
  // 	}
  // 	catch (error) {
  // 		logger.debug('changeWebcam() | failed: %o', error);
  // 	}
  // }

  async enableShare(encParam: QRTCVideoEncParams) {
    logger.debug(`enableShare()`);

    if (this._shareProducer) return;
    //else if (this._webcamProducer)
    //await this.disableWebcam();
    
    //先判断是否有sendTransport
    await this._createSendTransport();

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.debug("enableShare() | cannot produce video");

      return;
    }
    let track;
    try {
      logger.debug("enableShare() | calling getUserMedia()");
      let resolutionInfo = this.getResolutionInfo(encParam.videoResolution);
      // 录屏(屏幕共享)
      var stream: any = null;
      try {
        stream = await (navigator.mediaDevices as any).getDisplayMedia({
          audio: false,
          video: {
            displaySurface: "monitor",
            logicalSurface: true,
            cursor: true,
            width: { max: resolutionInfo.resWidth },
            height: { max: resolutionInfo.resHeight },
            frameRate: { max: encParam.videoFps },
          },
        });
      } catch (error: any) {
        //logger.debug(123456, error.name, error.message)
        if (error.name === "NotAllowedError") {
          this.safeEmit("error", { code: -1308 });
        } else {
          this.safeEmit("error", { code: -1309 });
        }
      }

      // May mean cancelled (in some implementations).
      if (!stream) {
        return;
      }

      track = stream.getVideoTracks()[0];

      let encodings;
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };

      if (this._forceH264) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
          (c) => c.mimeType.toLowerCase() === "video/h264"
        );

        if (!codec) {
          throw new Error("desired H264 codec+configuration is not supported");
        }
      }

      if (this._enableSharingLayers && resolutionInfo.canScaleDown) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec =
          this._mediasoupDevice.rtpCapabilities.codecs.find(
            (c) => c.kind === "video"
          );

        let encodings;
        //let encodings;
        let simulcastInfo = [
          {
            scaleResolutionDownBy: resolutionInfo.scaleDownBy,
            maxBitrate: resolutionInfo.maxDownBitrate * 1000,
          },
          {
            scaleResolutionDownBy: 1,
            maxBitrate: resolutionInfo.maxUpBitrate * 1000,
          },
        ];

        logger.debug(`firstVideoCodec is not vp9...`);
        encodings = simulcastInfo.map((encoding) => ({
          ...encoding,
          dtx: true,
        }));

        this._shareProducer = await this._sendTransport.produce({
          track,
          encodings,
          codecOptions: {
            videoGoogleStartBitrate: 1000,
          },
          appData: {
            peerName: this._roomInfo.userName,
            peerId: this._roomInfo.userId,
            share: true,
          },
        });
      } else {
        let encodeInfo = [
          { maxBitrate: this._encoderParam.maxVideoBitrate * 1000 },
        ];
        encodings = encodeInfo;
        this._shareProducer = await this._sendTransport.produce({
          track,
          encodings,
          codecOptions,
          codec,
          appData: {
            peerName: this._roomInfo.userName,
            peerId: this._roomInfo.userId,
            share: true,
          },
        });
      }
      this.safeEmit("screenCaptureStarted");
      logger.debug(
        `startScreenShare using codec=${
          this._shareProducer.rtpParameters.codecs[0].mimeType.split("/")[1]
        }`
      );

      this._shareProducer.on("transportclose", () => {
        logger.debug(`shareProducer transportclose!`);
        this._shareProducer = null;
      });

      this._shareProducer.on("trackended", async () => {
        logger.debug(`shareProducer trackended!`);
        await this.disableShare().catch(() => {});
      });
    } catch (error: any) {
      logger.debug(`enableShare() | failed:${error}`);

      if (error.name !== "NotAllowedError") {
        //TODO 通知上层
      }

      if (track) track.stop();
    }
  }

  async disableShare() {
    logger.debug("disableShare()");

    if (!this._shareProducer) return;

    this._shareProducer.close();

    try {
      await this._protoo.request("closeProducer", {
        transportId: this._sendTransport.id,
        transportType: "webrtc",
        producerId: this._shareProducer.id,
      });
    } catch (error) {
      //TODO 通知上层
    }

    this.safeEmit("screenCaptureStoped");

    this._shareProducer = null;
  }

  async muteAudio() {
    logger.debug("muteAudio()");
  }

  async unmuteAudio() {
    logger.debug("unmuteAudio()");
  }

  async restartIce() {
    logger.debug("restartIce()");

    try {
      if (this._sendTransport) {
        const iceParameters = await this._protoo.request("restartIce", {
          transportId: this._sendTransport.id,
        });
        logger.debug(
          `restartIce iceParameters=${JSON.stringify(
            iceParameters
          )}`
        );
        await this._sendTransport.restartIce({ iceParameters });
      }

      //TODO 目前先只restart发送的,不restart接收的,后面有必要再遍历接收
      // if (this._recvTransport)
      // {
      // 	const iceParameters = await this._protoo.request(
      // 		'restartIce',
      // 		{ transportId: this._recvTransport.id });

      // 	await this._recvTransport.restartIce({ iceParameters });
      // }
    } catch (error) {
      logger.debug(`restartIce() | failed:${error}`);
    }
  }

  async setMaxSendingSpatialLayer(spatialLayer: any) {
    try {
      if (this._webcamProducer)
        await this._webcamProducer.setMaxSpatialLayer(spatialLayer);
      else if (this._shareProducer)
        await this._shareProducer.setMaxSpatialLayer(spatialLayer);
    } catch (error) {
      logger.debug(`setMaxSendingSpatialLayer() | failed:${error}`);
    }
  }

  async setConsumerPreferredLayers(
    consumerId: any,
    spatialLayer: any,
    temporalLayer: any
  ) {
    try {
      await this._protoo.request("setConsumerPreferredLayers", {
        consumerId,
        spatialLayer,
        temporalLayer,
      });
    } catch (error) {
      logger.debug(`setConsumerPreferredLayers() | failed:${error}`);
    }
  }

  async setConsumerPriority(consumerId: any, priority: any) {
    try {
      await this._protoo.request("setConsumerPriority", {
        consumerId,
        priority,
      });
    } catch (error) {
      logger.debug(`setConsumerPriority() | failed:${error}`);
    }
  }

  async requestConsumerKeyFrame(consumerId: any) {
    logger.debug(`requestConsumerKeyFrame() [consumerId:${consumerId}]`);

    try {
      await this._protoo.request("requestConsumerKeyFrame", { consumerId });
    } catch (error) {
      logger.debug(`requestConsumerKeyFrame() | failed:${error}`);
    }
  }

  async sendCustomCmdMsg(cmdId: number, data: string) {
    await this._protoo.notify("customCmdMsg", {
      peerId: this._roomInfo.userId,
      cmdId: cmdId,
      seq: 1,
      data: data,
    });
  }
  async sendHostLeaveMsg(orderId:number, data:string) {
    console.log("entering sendHostLeaveMsg")
    await  this._protoo.request("hostLeaveMsg", {
      peerId: this._roomInfo.userId,
      orderId: orderId,
      seq: 1,
      data: data,
    });
     
    await this._protoo.notify("hostLeaveMsg", {
      peerId: this._roomInfo.userId,
      orderId: orderId,
      seq: 1,
      data: data,
    });
  }

  async sendChatMessage(text: string) {
    logger.debug(`sendChatMessage() [text:${text}]`);

    if (!this._chatDataProducer) {
      return;
    }

    try {
      this._chatDataProducer.send(text);
    } catch (error) {
      logger.debug(`chat DataProducer.send() failed:${error}`);
    }
  }

  async sendBotMessage(text: string) {
    if (!this._botDataProducer) {
      return;
    }

    try {
      this._botDataProducer.send(text);
    } catch (error) {
      logger.debug(`bot DataProducer.send() failed:${error}`);
    }
  }

  async changeDisplayName(displayName: string) {
    logger.debug(`changeDisplayName() [displayName:${displayName}]`);

    // Store in cookie.
    cookiesManager.setUser({ displayName });

    try {
      await this._protoo.request("changeDisplayName", { displayName });

      this._roomInfo.userName = displayName;
    } catch (error) {
      logger.debug(`changeDisplayName() | failed: ${error}`);
    }
  }

  async getSendTransportRemoteStats() {
    logger.debug("getSendTransportRemoteStats()");

    if (!this._sendTransport) return;

    return this._protoo.request("getTransportStats", {
      transportId: this._sendTransport.id,
    });
  }

  async getAudioRemoteStats() {
    logger.debug("getAudioRemoteStats()");

    if (!this._micProducer) return;

    return this._protoo.request("getProducerStats", {
      producerId: this._micProducer.id,
    });
  }

  async getVideoRemoteStats() {
    logger.debug("getVideoRemoteStats()");

    const producer = this._webcamProducer || this._shareProducer;

    if (!producer) return;

    return this._protoo.request("getProducerStats", {
      producerId: producer.id,
    });
  }

  async getConsumerRemoteStats(consumerId: any) {
    logger.debug("getConsumerRemoteStats()");

    const consumer = this._consumers.get(consumerId);

    if (!consumer) return;

    return this._protoo.request("getConsumerStats", { consumerId });
  }

  async getChatDataProducerRemoteStats() {
    logger.debug("getChatDataProducerRemoteStats()");

    const dataProducer = this._chatDataProducer;

    if (!dataProducer) return;

    return this._protoo.request("getDataProducerStats", {
      dataProducerId: dataProducer.id,
    });
  }

  async getBotDataProducerRemoteStats() {
    logger.debug("getBotDataProducerRemoteStats()");

    const dataProducer = this._botDataProducer;

    if (!dataProducer) return;

    return this._protoo.request("getDataProducerStats", {
      dataProducerId: dataProducer.id,
    });
  }

  async getDataConsumerRemoteStats(dataConsumerId: any) {
    logger.debug("getDataConsumerRemoteStats()");

    const dataConsumer = this._dataConsumers.get(dataConsumerId);

    if (!dataConsumer) return;

    return this._protoo.request("getDataConsumerStats", { dataConsumerId });
  }

  async getSendTransportLocalStats() {
    logger.debug("getSendTransportLocalStats()");

    if (!this._sendTransport) return null;

    return this._sendTransport.getStats();
  }

  async getAudioLocalStats() {
    logger.debug("getAudioLocalStats()");

    if (!this._micProducer) return null;

    return this._micProducer.getStats();
  }

  async getVideoLocalStats() {
    logger.debug("getVideoLocalStats()");

    const producer = this._webcamProducer || this._shareProducer;

    if (!producer) return null;

    return producer.getStats();
  }

  async getConsumerLocalStats(consumerId: any) {
    const consumer = this._consumers.get(consumerId);

    if (!consumer) return null;

    return consumer.getStats();
  }

  async applyNetworkThrottle({ uplink, downlink, rtt, secret }: any) {
    try {
      await this._protoo.request("applyNetworkThrottle", {
        uplink,
        downlink,
        rtt,
        secret,
      });
    } catch (error) {
      logger.debug(`applyNetworkThrottle() | failed:${error}`);
    }
  }

  async resetNetworkThrottle({ silent = false, secret }: any) {
    logger.debug("resetNetworkThrottle()");

    try {
      await this._protoo.request("resetNetworkThrottle", { secret });
    } catch (error) {
      if (!silent) {
        logger.debug(`resetNetworkThrottle() | failed:${error}`);
      }
    }
  }

  async _enterRoom() {
      this._mediasoupDevice = new mediasoupClient.Device({
        handlerName: this._handlerName as BuiltinHandlerName,
      });
      //100
      // const routerRtpCapabilities2 =
      // 	await this._protoo.request('getRouterRtpCapabilities');
      //ours
      const routerRtpCapabilities2 = await this._protoo.request(
        "getRouterRtpCapabilities",
        {
          sdkAppId: this._roomInfo!.sdkAppId,
          streamId: this._roomInfo!.userId,
          tokenId: this._roomInfo!.xConferenceToken,
        }
      );

      const routerRtpCapabilities = routerRtpCapabilities2.rtpCapabilities;

    try {
      await this._mediasoupDevice.load({ routerRtpCapabilities });
    } catch (error) {
      let enterError = new QRTCError("请求进房超时，请检查网络", "", QRTCErrorInfo.ERR_ENTER_ROOM_TIMEOUT);
      throw enterError;
    }
      
      // NOTE: Stuff to play remote audios due to browsers' new autoplay policy.
      //
      // Just get access to the mic and DO NOT close the mic track for a while.
      // Super hack!
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const audioTrack = stream.getAudioTracks()[0];

        audioTrack.enabled = false;

        setTimeout(() => audioTrack.stop(), 120000);
      }catch(error:any){
        let audioError = new QRTCError(error.name, error.message, QRTCErrorInfo.ERR_MIC_START_FAIL);
        this.handleGetUserMediaError(audioError, 'audio');
      }

      try {
        let joinedInfo = await this._protoo.request("join", {
          displayName: this._roomInfo.userName,
          device: this._device,
          rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
          sctpCapabilities: undefined, // this._useDataChannel && this._mediasoupDevice.sctpCapabilities
          xConferenceToken: this._roomInfo.xConferenceToken,
          avatarUrl: this._roomInfo.avatarUrl,
          acceptLanguage: this._roomInfo.acceptLanguage,
        });

        // console.error(`[RoomClient Bug] enterRoom joinedInfo=${JSON.stringify(joinedInfo)}`);
         console.log(joinedInfo)
        const { peerList, voiceList, selfTimeMs } = joinedInfo;

        this.safeEmit("enterRoom", { selfTimeMs });

        const needQueryTrack = [];

        for (const peer of peerList) {
          if (peer.peerId === this._roomInfo.userId) {
            continue;
          }
          this._peers.set(peer.peerId, peer);

          logger.debug(
            `remoteUserEnterRoom: userId=${peer.peerId} userName=${peer.displayName}`
          );
          this.safeEmit("remoteUserEnterRoom", {
            userId: peer.peerId,
            userName: peer.displayName,
            userDevice: peer.device,
            timeMs: peer.timeMs,
            avatarUrl: peer.avatarUrl,
          });

          const simpleTrack = peer.simpleTrack;
          if (
            simpleTrack &&
            (simpleTrack.audio || simpleTrack.video || simpleTrack.share)
          ) {
            needQueryTrack.push(peer.peerId);
          }
        }

        logger.debug(
          `joined needQueryTrack:${JSON.stringify(needQueryTrack)}`
        );

        if (needQueryTrack.length > 0) {
          this._queryPeerListTrack(needQueryTrack);
        }

        if (Array.isArray(voiceList) && voiceList.length > 0) {
          for (let j = 0; j < voiceList.length; j++) {
            const item = voiceList[j].voiceEnergy;

            this.safeEmit("userSpeaking", {
              userId: item.peerID,
              action: item.action,
            });
          }
        }
      } catch (error) {
        //this.safeEmit('errorMsg', { code: YRTCErrorInfo.ERR_ENTER_ROOM_TIMEOUT, errorData: { msg: "请求进房超时，请检查网络" } });
        //let enterError = new YRTCError("请求进房超时，请检查网络", "", YRTCErrorInfo.ERR_ENTER_ROOM_TIMEOUT);
        //reject(error);
        let enterError = new QRTCError("请求进房超时，请检查网络", "", QRTCErrorInfo.ERR_ENTER_ROOM_TIMEOUT);
        throw enterError;
      }
      //定时获取producer consumer stats
      var that = this;
      var counter = 0;
      this._audioLevelQueryInterval = setInterval(async function () {
        //webcamproducer
        if (that._webcamProducer) {
          if (!that._webcamProducer.closed) {
            const webcamProducerStats = await that._webcamProducer.getStats();
            //logger.debug(`[RoomClient100] webcamProducerStats=${webcamProducerStats}`)
            webcamProducerStats.forEach(function (value, key, map) {
              //that.reportLog(`[RoomClient100] webcamProducerKey=${key} webcamProducerValue=${JSON.stringify(value)}`)
            });
          }
        }
        that._consumers.forEach(async function (value, key, map) {
          //value和key就是map的key，value，map是_needConsumes本身
          let consumer = value;
          let consumerStats = await that.getConsumerLocalStats(consumer.id);
          let userVolumes: QRTCVolumeInfo[] = [];
          let totalVolume: number = 0;
          consumerStats.forEach(function (value, key, map) {
            //TODO too much logs
            //logger.debug(`consumerStats:${JSON.stringify(value)}`)
            if (value.type == "track") {
              //读取音量,默认1-100 如果audioLevel小于0.01,则认为是静音
              let audioLevel = value.audioLevel; //0.0004272591326639607
              if (audioLevel > 0.01) {
                let audioLevelInt = Math.round(audioLevel * 100);
                let audioVolumeInfo: QRTCVolumeInfo = {
                  userId: consumer.appData.peerId as string,
                  volume: audioLevelInt,
                };
                totalVolume += audioLevelInt;
                userVolumes.push(audioVolumeInfo);
              }
            }
          });
          //total
          that.safeEmit("userVoiceVolume", {
            userVolumes,
            totalVolume: totalVolume > 100 ? 100 : totalVolume,
          });
        });
        //计算上行和下行丢包率
        let upQuality: QRTCQuality = QRTCQuality.QRTCQuality_Unknown;
        let downQuality: QRTCQuality = QRTCQuality.QRTCQuality_Unknown;
        if (counter == 1000) {
          counter = 0;
        }
        counter++;
        if (counter % 5 == 0) {
          //定时获取上行的丢包率
          if (that._sendTransport) {
            let sendPacketlost = 0;
            let packetsSent = 0;
            let localstats = await that.getSendTransportLocalStats();
            for (var [key, value] of localstats) {
              //logger.debug(`[APP] localstats key=${key} value=${JSON.stringify(value)}`)
              if (key.startsWith("RTCRemoteInboundRtp")) {
                sendPacketlost += value.packetsLost;
                //获取丢包数量以及时间戳 timestamp已经是以ms为单位了,不需要再乘以1000了
              } else if (key.startsWith("RTCTransport")) {
                packetsSent += value.packetsSent;
              }
            }
            //计算平均上行的质量(上行平均丢包率)
            let packetsSentPerSecond =
              packetsSent - that._sendTransport.lastpacketsSentOrRecv;
            if (packetsSentPerSecond > 0) {
              let averagePacketLost =
                ((sendPacketlost - that._sendTransport.lastpacketlost) /
                  packetsSentPerSecond) *
                100;
              logger.debug(
                `uplink averagePacketLost=${averagePacketLost}`
              );
              switch (true) {
                case averagePacketLost <= 0.001:
                  upQuality = QRTCQuality.QRTCQuality_Excellent;
                  break;
                case averagePacketLost > 0.001 && averagePacketLost <= 2:
                  upQuality = QRTCQuality.QRTCQuality_Good;
                  break;
                case averagePacketLost > 2 && averagePacketLost <= 5:
                  upQuality = QRTCQuality.QRTCQuality_Poor;
                  break;
                case averagePacketLost > 5 && averagePacketLost <= 10:
                  upQuality = QRTCQuality.QRTCQuality_Bad;
                  break;
                case averagePacketLost > 10 && averagePacketLost <= 20:
                  upQuality = QRTCQuality.QRTCQuality_Vbad;
                  break;
                case averagePacketLost > 10 && averagePacketLost > 20:
                  upQuality = QRTCQuality.QRTCQuality_Down;
                  break;
              }
            }
            that._sendTransport.lastpacketlost = sendPacketlost;
            that._sendTransport.lastpacketsSentOrRecv = packetsSent;
          }

          //定时获取下行的丢包率
          let allAveragePacketLost = 0; //所有recvTransport的每秒丢包率总和
          for (var [
            recvTransportKey,
            recvTransportValue,
          ] of that._recvTransports) {
            let remoteStats = await recvTransportValue.getStats();
            let recvPacketLost = 0;
            let recvPacketsReceived = 0;

            //从中寻找
            for (var [remoteStatKey, remoteStatValue] of remoteStats) {
              if (remoteStatKey.startsWith("RTCInboundRTP")) {
                recvPacketLost += remoteStatValue.packetsLost;
              } else if (remoteStatKey.startsWith("RTCTransport")) {
                recvPacketsReceived += remoteStatValue.packetsReceived;
              }
            }
            //每秒收包的数量
            let packetsReceivedPerSecond =
              recvPacketsReceived - recvTransportValue.lastpacketsSentOrRecv;
            if (packetsReceivedPerSecond > 0) {
              let averagePacketLost =
                (recvPacketLost - recvTransportValue.lastpacketlost) /
                packetsReceivedPerSecond;
              allAveragePacketLost += averagePacketLost;
            }
            recvTransportValue.lastpacketlost = recvPacketLost;
            recvTransportValue.lastpacketsSentOrRecv = recvPacketsReceived;
          }
          //计算所有接收transport的平均丢包率
          let recvAveragePacketLost =
            (allAveragePacketLost / that._recvTransports.size) * 100;
          // logger.debug(
          //   `downlink recvAveragePacketLost=${recvAveragePacketLost}`
          // );
          switch (true) {
            case recvAveragePacketLost <= 0.001:
              downQuality = QRTCQuality.QRTCQuality_Excellent;
              break;
            case recvAveragePacketLost > 0.001 && recvAveragePacketLost <= 2:
              downQuality = QRTCQuality.QRTCQuality_Good;
              break;
            case recvAveragePacketLost > 2 && recvAveragePacketLost <= 5:
              downQuality = QRTCQuality.QRTCQuality_Poor;
              break;
            case recvAveragePacketLost > 5 && recvAveragePacketLost <= 10:
              downQuality = QRTCQuality.QRTCQuality_Bad;
              break;
            case recvAveragePacketLost > 10 && recvAveragePacketLost <= 20:
              downQuality = QRTCQuality.QRTCQuality_Vbad;
              break;
            case recvAveragePacketLost > 10 && recvAveragePacketLost > 20:
              downQuality = QRTCQuality.QRTCQuality_Down;
              break;
          }

          //回调给上层
          //根据averagePacketLost来进行评分
          let qualityInfo = {
            uplink: upQuality,
            downlink: downQuality,
          };
          // logger.debug(
          //   `networkQuality:${JSON.stringify(qualityInfo)}`
          // );
          that.safeEmit("networkQuality", qualityInfo);
        }
      }, 20000); //改为200ms
    // } catch (error) {
    //   this.safeEmit("error", `join room failed ${error}`);
    //   logger.debug(`catch error=${error}`);

    //   this.close();
    // }
  }

  handleGetUserMediaError(error:QRTCError, type:string){
		if (error.errorName === 'OverconstrainedError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_START_FAIL, errorData: { msg: "分辨率不支持" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_START_FAIL, errorData: { msg: "麦克风OverconstrainedError" } })
			}
		} else if (error.errorName === 'AbortError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_NO_GRANT;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_NO_GRANT, errorData: { msg: "摄像头设备AbortError" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_NO_GRANT;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_NO_GRANT, errorData: { msg: "麦克风设备AbortError" } })
			}
		}else if (error.errorName === 'NotAllowedError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_NO_GRANT;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_NO_GRANT, errorData: { msg: "摄像头设备未授权，可能是权限被用户拒绝了" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_NO_GRANT;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_NO_GRANT, errorData: { msg: "麦克风设备未授权，可能是权限被用户拒绝了" } })
			}
		}else if (error.errorName === 'NotFoundError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_START_FAIL, errorData: { msg: "摄像头设备未找到" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_START_FAIL, errorData: { msg: "麦克风设备未授权，可能是权限被用户拒绝了" } })
			}
		}else if (error.errorName === 'NotReadableError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_OCCUPIED;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_OCCUPIED, errorData: { msg: "打开摄像头失败" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_OCCUPIED;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_OCCUPIED, errorData: { msg: "打开麦克风失败" } })
			}
		}else if (error.errorName === 'SecurityError') {
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_START_FAIL, errorData: { msg: "摄像头设备不符合安全条件" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_START_FAIL, errorData: { msg: "麦克风设备不符合安全条件" } })
			}
		}else{
			if (type === "video") {
				error.errorCode = QRTCErrorInfo.ERR_CAMERA_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_CAMERA_START_FAIL, errorData: { msg: "摄像头未知错误" } })
			} else {
				error.errorCode = QRTCErrorInfo.ERR_MIC_START_FAIL;
				this.safeEmit('errorMsg', { code: QRTCErrorInfo.ERR_MIC_START_FAIL, errorData: { msg: "麦克风未知错误" } })
			}
		}

	}

  _queryPeerListTrack(needQueryTrack: any) {
    const splitTrackArr = this._splitQueryTrack(needQueryTrack, 50);

    logger.debug(splitTrackArr);

    splitTrackArr.forEach((arr) => {
      this._protoo
        .request("queryUser", {
          peerId: arr,
        })
        .then((res: any) => {
          logger.debug(res.peerList);

          for (const queryPeer of res.peerList) {
            for (const track of queryPeer.track) {
              // logger.debug(`joined get track:${JSON.stringify(track)}`)
              const { producerId, kind, paused, appData } = track;

              //这个要存一下
              this._needConsumes.set(producerId, {
                peerId: queryPeer.peerId,
                producerId,
                kind,
                appData,
              });

              let emitMsgAvailable =
                appData.share != undefined
                  ? "userShareAvailable"
                  : kind == "video"
                  ? "userVideoAvailable"
                  : "userAudioAvailable";

              if (paused) {
                //远端暂停,意味着远端这个视频不存在,因此不处理
                logger.debug(
                  `after enterRoom, there is track paused!`
                );

                if (emitMsgAvailable === "userShareAvailable") {
                  this.safeEmit(emitMsgAvailable, {
                    userId: queryPeer.peerId,
                    available: true,
                    paused: true,
                    producerId,
                    appData,
                  });
                }
              } else {
                this.safeEmit(emitMsgAvailable, {
                  userId: queryPeer.peerId,
                  available: true,
                  producerId,
                  appData,
                });
              }
            }
          }
        })
        .catch((error: any) => {
          logger.debug(error);
        });
    });
  }

  _splitQueryTrack(data: any, num: number) {
    let index = 0;
    let arr = [];
    while (index < data.length) {
      arr.push(data.slice(index, (index += num)));
    }

    return arr;
  }

  //sdk提供接口getPageUserInfo(pageNo:页码)，上层调用此接口,传入要获取信息的页码,sdk层通过该页码，
  //向服务器请求对应信息(getPeerList)，此处sdk需要在遍历peerlist的时候，与之前的用户列表进行一下
  //去重操作，并通过(queryUser)获取对应媒体信息,最后通过OnRemoteUserEnterRoom也将信息回调给上层
  async getPageUserInfo(pageNo: number) {
    let pageUserInfo = await this._protoo.request("getPeerList", {
      page: pageNo,
    });
    logger.debug(
      `getPageUserInfo:${JSON.stringify(pageUserInfo)}`
    );
    const {
      peerNumberInRoom,
      peerPages,
      currentPage,
      peerNumberInCurrentPage,
      peerList,
    } = pageUserInfo;
    //先通知总量等信息
    this.safeEmit("pageUserInfo", {
      peerNumberInRoom, //总人数
      peerPages, //总页数
      peerNumberInCurrentPage, //每页人数
      currentPage, //当前页码
    });
    //再通知具体信息
    //let peerIdList = [];
    for (const peer of peerList) {
      if (peer.id == this._roomInfo.userId) {
        continue;
      }
      //先判断是否重复
      //如果重复则不通知上层
      //没重复则加入map并通知上层
      if (!this._peers.has(peer.id)) {
        //peerIdList.push(peer.id);
        this._peers.set(peer.id, peer);
        //通知上层
        this.safeEmit("remoteUserEnterRoom", {
          userId: peer.id,
          userName: peer.displayName,
          userDevice: peer.device,
          timeMs: peer.timeMs,
          avatarUrl: peer.avatarUrl,
        });
        //遍历该peer的track信息
        for (const track of peer.track) {
          logger.debug(
            `joined get track:${JSON.stringify(track)}`
          );
          const { producerId, kind, paused, appData } = track; //TODO by pcg 需要让服务器加上这个appData
          //这个要存一下
          this._needConsumes.set(producerId, {
            peerId: peer.peerId,
            producerId,
            kind,
            appData,
          });
          let emitMsgAvailable =
            appData.share != undefined
              ? "userShareAvailable"
              : kind == "video"
              ? "userVideoAvailable"
              : "userAudioAvailable";

          if (paused) {
            //远端暂停,意味着远端这个视频不存在,因此不处理
            logger.debug(
              `after enterRoom, there is track paused!`
            );
          } else {
            this.safeEmit(emitMsgAvailable, {
              userId: peer.peerId,
              available: true,
            });
          }
        }
      }
    }
    //此处与服务器沟通后得知getpeerlist已经返回了track信息,因此后面先不用处理了
    //然后查询对应的媒体信息 通知上层video audio available等信息
    // let userQueryInfo = await this._protoo.request(
    // 	'queryUser',
    // 	{
    // 		//要获取track信息的peerIdList
    // 		peerId: peerIdList //["345", "123"]
    // 	});
    // logger.debug(`userQueryInfo:${JSON.stringify(userQueryInfo)}`)
    //const userList= userQueryInfo.peerList;
    //TODO 服务器接口这里很别扭,应该直接上面就给出track信息了
    // for (const user of userList) {
    // 	if(user.peerId == this._roomInfo.userId){
    // 		continue
    // 	}
    // 	if(user.track == undefined){
    // 		continue
    // 	}
    // 	for (const track of user.track) {
    // 		logger.debug(`joined get track:${JSON.stringify(track)}`)
    // 		const { producerId, kind, paused, appData } = track;//TODO by pcg 需要让服务器加上这个appData
    // 		//这个要存一下
    // 		this._needConsumes.set(producerId, { peerId: user.peerId, producerId, kind, appData })
    // 		let emitMsgAvailable = appData.share != undefined ? "userShareAvailable" : (kind == 'video' ? "userVideoAvailable" : "userAudioAvailable")

    // 		if (paused) {
    // 			//远端暂停,意味着远端这个视频不存在,因此不处理
    // 			logger.debug(`getPageUserInfo, there is track paused!`)
    // 		} else {
    // 			this.safeEmit(emitMsgAvailable, {
    // 				userId: user.peerId,
    // 				available: true
    // 			})
    // 		}
    // 	}
    // }
  }

  async _updateWebcams() {
    logger.debug("_updateWebcams()");

    // Reset the list.
    this._webcams = new Map();

    logger.debug("_updateWebcams() | calling enumerateDevices()");

    const devices = await navigator.mediaDevices.enumerateDevices();

    for (const device of devices) {
      if (device.kind !== "videoinput") continue;

      this._webcams.set(device.deviceId, device);
    }

    const array = Array.from(this._webcams.values());
    const len = array.length;
    const currentWebcamId = this._webcam.device
      ? this._webcam.device.deviceId
      : undefined;

    logger.debug(`_updateWebcams() [webcams:${array}]`);

    if (len === 0) this._webcam.device = null;
    else if (!this._webcams.has(currentWebcamId))
      this._webcam.device = array[0];
  }

  _getWebcamType(device: any) {
    if (/(back|rear)/i.test(device.label)) {
      logger.debug("_getWebcamType() | it seems to be a back camera");

      return "back";
    } else {
      logger.debug("_getWebcamType() | it seems to be a front camera");

      return "front";
    }
  }

  async _pauseConsumer(consumer: any) {
    // if (consumer.paused)
    // 	return;
    try {
      await this._protoo.request("pauseConsumer", {
        transportId: consumer.recvTransportId,
        transportType: "webrtc",
        producerId: consumer.producerId,
        consumerId: consumer.id,
      });
      //consumer.resume();
    } catch (error) {
      logger.debug(`_resumeConsumer() | failed:${error}`);
    }
  }

  async _resumeConsumer(consumer: Consumer) {
    //根据闫哥测试,本地不需要进行暂停,都是发送信令暂停
    // if (!consumer.paused)
    // 	return;
    try {
      await this._protoo.request("resumeConsumer", {
        transportId: consumer.recvTransportId,
        transportType: "webrtc",
        producerId: consumer.producerId,
        consumerId: consumer.id,
      });
      //consumer.resume();
    } catch (error) {
      logger.debug(`_resumeConsumer() | failed:${error}`);
    }
  }

  getErrorInfo(code: Number) {
    switch (code) {
      case 16385:
        return { SIGNAL_CAHNNEL_SETUP_FAILED: "信令通道建立失败" };
      default:
        return {};
    }
  }

  //获取对应分辨率的信息,如能否支持simucast等等
  getResolutionInfo(resolution: QRTCVideoResolution): ResolutionInfo {
    let resInfo: ResolutionInfo = {
      resWidth: 1,
      resHeight: 1,
      canScaleDown: true,
      scaleDownBy: 2,
      maxUpBitrate: 300,
      maxDownBitrate: 100,
    };
    //需要限制住能simulcast的每一路流的最大码率
    switch (resolution) {
      case QRTCVideoResolution.QRTCVideoResolution_120_120:
        resInfo.resWidth = 120;
        resInfo.resHeight = 120;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_160_160:
        resInfo.resWidth = 160;
        resInfo.resHeight = 160;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_270_270:
        resInfo.resWidth = 270;
        resInfo.resHeight = 270;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_480_480:
        resInfo.resWidth = 480;
        resInfo.resHeight = 480;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_160_120:
        resInfo.resWidth = 160;
        resInfo.resHeight = 120;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_240_180:
        resInfo.resWidth = 240;
        resInfo.resHeight = 180;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_280_210:
        resInfo.resWidth = 280;
        resInfo.resHeight = 210;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_320_240:
        resInfo.resWidth = 320;
        resInfo.resHeight = 240;
        resInfo.canScaleDown = true;
        resInfo.maxDownBitrate = 150; //kbps
        resInfo.maxUpBitrate = 375;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_400_300:
        resInfo.resWidth = 400;
        resInfo.resHeight = 300;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_480_360:
        resInfo.resWidth = 480;
        resInfo.resHeight = 360;
        resInfo.canScaleDown = true;
        resInfo.maxDownBitrate = 225; //kbps
        resInfo.maxUpBitrate = 600;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_640_480:
        resInfo.resWidth = 640;
        resInfo.resHeight = 480;
        resInfo.canScaleDown = true;
        resInfo.scaleDownBy = 4;
        resInfo.maxDownBitrate = 150; //kbps
        resInfo.maxUpBitrate = 900;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_960_720:
        resInfo.resWidth = 960;
        resInfo.resHeight = 720;
        resInfo.canScaleDown = true;
        resInfo.scaleDownBy = 4;
        resInfo.maxDownBitrate = 200; //kbps
        resInfo.maxUpBitrate = 1100;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_160_90:
        resInfo.resWidth = 160;
        resInfo.resHeight = 90;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_256_144:
        resInfo.resWidth = 256;
        resInfo.resHeight = 144;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_320_180:
        resInfo.resWidth = 320;
        resInfo.resHeight = 180;
        resInfo.canScaleDown = true;
        resInfo.maxDownBitrate = 200; //kbps
        resInfo.maxUpBitrate = 300;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_480_270:
        resInfo.resWidth = 480;
        resInfo.resHeight = 270;
        resInfo.canScaleDown = false;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_640_360:
        resInfo.resWidth = 640;
        resInfo.resHeight = 360;
        resInfo.canScaleDown = true;
        resInfo.scaleDownBy = 4;
        resInfo.maxDownBitrate = 200; //kbps
        resInfo.maxUpBitrate = 600;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_960_540:
        resInfo.resWidth = 960;
        resInfo.resHeight = 540;
        resInfo.canScaleDown = true;
        resInfo.maxDownBitrate = 400; //kbps
        resInfo.maxUpBitrate = 900;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_1280_720:
        resInfo.resWidth = 1280;
        resInfo.resHeight = 720;
        resInfo.canScaleDown = true;
        resInfo.scaleDownBy = 4;
        resInfo.maxDownBitrate = 300; //kbps
        resInfo.maxUpBitrate = 1300;
        break;
      case QRTCVideoResolution.QRTCVideoResolution_1920_1080:
        resInfo.resWidth = 1920;
        resInfo.resHeight = 1080;
        resInfo.canScaleDown = true;
        resInfo.scaleDownBy = 4;
        resInfo.maxDownBitrate = 400; //kbps
        resInfo.maxUpBitrate = 2100;
        break;
      default:
        break;
    }
    return resInfo;
  }

  //开启设备变化监测
  async enableDevicesMonitor() {
    logger.debug("devicechanged");
    this._camDeviceList = await this.getCameraDeviceList();
    this._micDeviceList = await this.getMicDevicesList();
    this._speakerDeviceList = await this.getSpeakerDevicesList();
    //TODO 放在这里是否合适
    navigator.mediaDevices.addEventListener("devicechange", async (event) => {
      logger.debug(
        `devicechanged event=${JSON.stringify(event)}`
      );
      let newCameraList = await this.getCameraDeviceList();
      let newMicList = await this.getMicDevicesList();
      let newSpeakerList = await this.getSpeakerDevicesList();
      let subSet = [];
      let deviceEvent: QRTCMediaDeviceChangeEvent =
        QRTCMediaDeviceChangeEvent.QRTCMediaDevice_Unknown;
      //let currentCameraDevice = newCameraList.find(obj => obj.deviceId == this._webcam.device.deviceId);
      if (newCameraList.length !== this._camDeviceList.length) {
        subSet = getSubSet(
          newCameraList as [any],
          this._camDeviceList as [any]
        );
        deviceEvent =
          newCameraList.length > this._camDeviceList.length
            ? QRTCMediaDeviceChangeEvent.QRTCMediaDevice_WebCamAdded
            : QRTCMediaDeviceChangeEvent.QRTCMediaDevice_WebCamRemoved;
        this.safeEmit("deviceChanged", {
          event: deviceEvent,
          deviceId: subSet[0].deviceId,
        });
        logger.debug(
          `cam changed deviceId=${subSet[0].deviceId}`
        );
      }
      if (newMicList.length !== this._micDeviceList.length) {
        subSet = getSubSet(newMicList as [any], this._micDeviceList as [any]);
        deviceEvent =
          newMicList.length > this._micDeviceList.length
            ? QRTCMediaDeviceChangeEvent.QRTCMediaDevice_MicAdded
            : QRTCMediaDeviceChangeEvent.QRTCMediaDevice_MicRemoved;
        this.safeEmit("deviceChanged", {
          event: deviceEvent,
          deviceId: subSet[0].deviceId,
        });
        logger.debug(
          `mic changed deviceId=${subSet[0].deviceId}`
        );
      }
      if (newSpeakerList.length !== this._speakerDeviceList.length) {
        subSet = getSubSet(
          newSpeakerList as [any],
          this._speakerDeviceList as [any]
        );
        deviceEvent =
          newSpeakerList.length > this._speakerDeviceList.length
            ? QRTCMediaDeviceChangeEvent.QRTCMediaDevice_SpeakerAdded
            : QRTCMediaDeviceChangeEvent.QRTCMediaDevice_SpeakerRemoved;
        this.safeEmit("deviceChanged", {
          event: deviceEvent,
          deviceId: subSet[0].deviceId,
        });
        logger.debug(
          `speaker changed deviceId=${subSet[0].deviceId}`
        );
      }

      this._camDeviceList = newCameraList;
      this._micDeviceList = newMicList;
      this._speakerDeviceList = newSpeakerList;
    });
  }

  addAudioTrack(track:MediaStreamTrack){
    if(this._multiTrackAudioplayer){
      this._multiTrackAudioplayer.addTrack(track)
    }
  }

  removeAudioTrack(track:MediaStreamTrack){
    if(this._multiTrackAudioplayer){
      this._multiTrackAudioplayer.removeTrack(track)
    }
  }
  //#################################私有函数结束 SDK内部调用#############################################

  //#################################SDK静态函数###############################
  static audioContext: AudioContext = null;
  static soundMeter: SoundMeter = null;
  static micTestVolume: number = 0;
  static micTestVolumeQueryInterval: any = null;
  static micStream: any = null;
  //启动麦克风音量检测
  static async enableMicTest() {
    try {
      //实例化AudioContext
      this.audioContext = new AudioContext();
    } catch (e) {
      logger.debug("网页音频API不支持.");
    }

    //SoundMeter声音测量,用于做声音音量测算使用的
    this.soundMeter = new SoundMeter(this.audioContext);

    const constraints = {
      //启用音频
      audio: true,
      //禁用视频
      video: false,
    };
    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const audioTrack = this.micStream.getAudioTracks()[0];
      //将声音测量对象与流连接起来
      this.soundMeter.connectToSource(this.micStream);
      //开始实时读取音量值  每隔200毫秒调用一次soundMeterProcess函数,模拟实时检测音频音量
      this.micTestVolumeQueryInterval = setInterval(() => {
        //读取音量值,再乘以一个系数,可以得到音量条的宽度
        let instance: number = this.soundMeter.instant;
        this.micTestVolume = instance * 348;
        logger.debug(`micTestVolume = ${this.micTestVolume}`);
      }, 500);
    } catch (error) {}
  }
  //获取麦克风音量 范围0-348
  static getMicTestVolume() {
    return this.micTestVolume;
  }
  //停止麦克风音量检测
  static async disableMicTest() {
    //关闭定时器
    if (this.micTestVolumeQueryInterval) {
      clearInterval(this.micTestVolumeQueryInterval);
      this.micTestVolumeQueryInterval = null;
    }
    //断开sound meter
    this.soundMeter.stop();
    //关闭stream
    if (this.micStream) {
      var tracks, i, len;
      //判断是否有getTracks方法
      if (this.micStream.getTracks) {
        //获取所有的Track
        tracks = this.micStream.getTracks();
        //迭代所有Track
        for (i = 0, len = tracks.length; i < len; i += 1) {
          //停止每个Track
          tracks[i].stop();
        }
      } else {
        //获取所有的音频Track
        tracks = this.micStream.getAudioTracks();
        //迭代所有音频Track
        for (i = 0, len = tracks.length; i < len; i += 1) {
          //停止每个Track
          tracks[i].stop();
        }
      }
    }
    //变量重置
    this.audioContext = null;
    this.soundMeter = null;
    this.micTestVolume = 0;
    this.micStream = null;
  }

  //测试连接公网的某个ip(比如www.baidu.com)的连接情况和耗时
  static testHttpConnection(httpUrl?: string) {
    var p = new Promise(function (resolve, reject) {
      ping(httpUrl ? httpUrl : "https://baidu.com", 0.3)
        .then(function (delta: any) {
          logger.debug("Ping time was " + String(delta) + " ms"); //单位ms
          resolve({ pingTime: delta }); //单位ms
        })
        .catch(function (err: any) {
          console.error("Could not ping remote URL", err);
          reject(err);
        });
    });
    return p;
  }

  //测试websocket连接情况和耗时
  static testWSConnection(wsUrl: string) {
    var p = new Promise(function (resolve, reject) {
      const protooTransport = new protooClient.WebSocketTransport(wsUrl, {});
      protooTransport.on("open", () => {});
      protooTransport.on("connectionTimeOut", () => {});
      protooTransport.on("error", () => {});
    });
    return p;
  }
}
